/**
 * Typed mock fixtures for the IVT Hub frontend.
 *
 * Every fixture is annotated with the GENERATED Smithy output type from
 * `@amzn/ivthub-client`, so `tsc` pins the mock shape to the real API contract.
 * When the backend op/field changes, the generated type changes, and these
 * fixtures stop type-checking — that is the point: the mock can never silently
 * drift away from the contract a page renders against.
 *
 * ⚠️ ALL VALUES ARE SYNTHETIC. Nothing here is copied from a real account, opp,
 * or SFDC export — the shapes are modeled on the prototype's UI, but every id,
 * name, alias, and number is fabricated and deliberately self-identifying as
 * mock (e.g. `MOCK-OPP-001`, `MOCK-ACCT-001`, aliases alice/bob). Do NOT paste
 * real SFDC ids (`006…`/`001…` opp/account ids), real account names, or real
 * customer ARR into this file — keep mock data un-mistakable for production data
 * so it can never trip a data-leak review (see the SFDC-id-in-git SEV2).
 *
 * The values are chosen to exercise the business-rule BOUNDARIES the UI encodes
 * (see comments per fixture), so these fixtures double as the inputs for
 * "revert-the-rule-and-it-fails" component tests.
 */

import type {
  ListAssetsOutput,
  GetAssetOutput,
  GetSolutionMapOutput,
  GetSolutionMapAllOutput,
  ListIndustriesOutput,
  GetConfigurationOutput,
  UpdateConfigurationOutput,
  IndustryGrants,
  ListTopicsOutput,
  UpsertTopicOutput,
  UpsertTopicInstanceOutput,
  GetUcPipelineDetailOutput,
  GetUcFullOutput,
  GetPipelineBubblesOutput,
  UpdateAssetOutput,
  LinkUcAssetOutput,
  UpsertUcTagByIdOutput,
  ManageArsenalOutput,
  CreateMotionOutput,
  ListMotionsOutput,
  ListTaggedPipelinesOutput,
  GetOppActivitiesOutput,
  GetKbTreeOutput,
  RequestKbFileUploadUrlOutput,
  RequestKbOverviewUploadUrlOutput,
  ConfirmKbFileUploadOutput,
  GetAssetAssessmentOutput,
  ListStorylinesOutput,
  GetStorylineOutput,
  ListAccountPipelineOutput,
  ListAssetOpportunitiesOutput,
  ListAccountFlagsOutput,
  PutOppAssetDismissalOutput,
  DeleteOppAssetDismissalOutput,
  PutAccountFlagOutput,
  DeleteAccountFlagOutput,
  AccountCandidate,
  ListRadarDirectionsOutput,
  GetRadarDirectionOutput,
  // Radar Admin (R16) — one list payload per industry per shape, so
  // VITE_IVT_MOCK=1 lets the Admin page round-trip create/update/soft-
  // delete against stateful fixtures. The read fixtures type against the
  // List* outputs; the mutating ops are served by MockIvtHubClient's own
  // state (see mockClient.ts), so no Upsert/SetStatus fixtures are needed
  // here.
  ListIndustryTopicsOutput,
  GetIndustryGeneralTopicOutput,
  ListIndustryNewsSourcesOutput,
  RadarStandardTopic,
  RadarGeneralTopic,
  RadarNewsSource,
} from '../client/IvtHubClient';
import type {
  GetAssetPipelineFlowOutput,
  GetIndustryPipelineFlowOutput,
} from '@amzn/ivthub-client';
// Radar enums as VALUES — pulled straight from the generated package, NOT from
// the local `../client/IvtHubClient` shim. The shim pulls MockIvtHubClient
// back into fixtures via mockClient, which creates a circular import; at
// setup-time (before vi.mock hoists) the local shim is still mid-evaluation
// and the enum references at module scope would land on undefined. Importing
// the enum values from `@amzn/ivthub-client` directly breaks the cycle while
// keeping the string-enum type safety (declaration merging carries the type).
import {
  RadarDirectionStatus,
  RadarEmptyNewsState,
  RadarEvidenceTier,
  RadarNeedEvidence,
  RadarNewsFetchMode,
  RadarNewsSourceKind,
  RadarSuggestedOrgSide,
} from '@amzn/ivthub-client';

// ── ListAssets ───────────────────────────────────────────────────────────────
// Boundary design (AssetRow health heuristic = elementCompleteness>=4 && arsenalCount>=2):
//   fsi-claims-auto      8/8, arsenal 4 → HEALTHY (no ⚠), SCALING ★
//   fsi-fraud-detection  4/8, arsenal 2 → HEALTHY exactly on the boundary, PLAYBOOK ●
//   game-liveops-cn      3/8, arsenal 5 → ⚠ (completeness below 4), BUILDING ◐
//   hcls-genomics        6/8, arsenal 1 → ⚠ (arsenal below 2), BUILDING ◐
//   rch-personalize      0/8, arsenal 0 → ⚠ (brand new), EXPLORING ○, no owner
export const listAssetsMock: ListAssetsOutput = {
  assets: [
    {
      assetId: 'fsi-claims-auto',
      // origin = ivt_incubated: all 5 mock assets are the New Assets (incubated)
      // slice of the unified-asset model. The page now passes this as a ListAssets
      // filter; the MockIvtHubClient does NOT filter by it (dispatch is by command
      // name only), so origin here is for TYPE-contract parity + the name-label
      // path, not server-side scoping — see mockClient.ts.
      origin: 'ivt_incubated',
      // name present → row/detail label renders `name` (exercises the name branch
      // of `asset.name ?? asset.useCase`). MOCK-prefixed, synthetic.
      name: 'MOCK 智能理赔自动化资产',
      industry: 'FSI',
      useCase: '智能理赔自动化',
      audience: '保险公司 CTO',
      ownerIs: 'alice',
      maturity: 'SCALING',
      elementCompleteness: 8,
      arsenalCount: 4,
      hasMotionProfile: true,
      motionAccountCount: 12,
      motionOppCount: 39,
      createdAt: '2025-11-02T08:00:00Z',
      updatedAt: '2026-05-30T08:00:00Z',
      // Storyline links (M2 "View by Storyline"): linked to the FSI 智能理赔 storyline.
      // Exercises: an asset appearing under a storyline leaf in the Menu tree.
      storylineLinks: [
        { storylineId: '11111111-1111-4111-8111-111111111111', storylineName: 'FSI 智能理赔叙事', industry: 'FSI' },
      ],
    },
    {
      assetId: 'fsi-fraud-detection',
      origin: 'ivt_incubated',
      // name UNSET → label falls back to useCase (exercises the legacy/UC fallback
      // branch the smithy doc describes).
      industry: 'FSI',
      useCase: '实时反欺诈',
      audience: '银行风控负责人',
      ownerIs: 'bob',
      maturity: 'PLAYBOOK',
      elementCompleteness: 4,
      arsenalCount: 2,
      hasMotionProfile: true,
      motionAccountCount: 0,
      motionOppCount: 82,
      createdAt: '2025-12-01T08:00:00Z',
      updatedAt: '2026-06-01T08:00:00Z',
    },
    {
      assetId: 'game-liveops-cn',
      origin: 'ivt_incubated',
      // name present → label renders `name`. MOCK-prefixed, synthetic.
      name: 'MOCK LiveOps 资产',
      industry: 'GAME',
      useCase: 'LiveOps 实时运营',
      audience: '游戏发行商运营总监',
      ownerIs: 'carol',
      maturity: 'BUILDING',
      elementCompleteness: 3,
      arsenalCount: 5,
      hasMotionProfile: false,
      motionAccountCount: 0,
      motionOppCount: 0,
      createdAt: '2026-01-15T08:00:00Z',
      updatedAt: '2026-06-10T08:00:00Z',
      // Linked to the GAME storyline. hcls-genomics / rch-personalize below carry NO
      // storylineLinks → they land in their industry's "Unassigned (N)" Menu node.
      storylineLinks: [
        { storylineId: '33333333-3333-4333-8333-333333333333', storylineName: 'GAME LiveOps 叙事', industry: 'GAME' },
      ],
    },
    {
      assetId: 'hcls-genomics',
      origin: 'ivt_incubated',
      // name UNSET → label falls back to useCase.
      industry: 'HCLS',
      useCase: '基因组学数据平台',
      audience: '生物科技研发 VP',
      ownerIs: 'dave',
      maturity: 'BUILDING',
      elementCompleteness: 6,
      arsenalCount: 1,
      hasMotionProfile: false,
      motionAccountCount: 0,
      motionOppCount: 0,
      createdAt: '2026-02-20T08:00:00Z',
      updatedAt: '2026-06-05T08:00:00Z',
    },
    {
      assetId: 'rch-personalize',
      origin: 'ivt_incubated',
      // name UNSET → label falls back to useCase.
      industry: 'RCH',
      useCase: '零售个性化推荐',
      audience: undefined,
      ownerIs: undefined,
      maturity: 'EXPLORING',
      elementCompleteness: 0,
      arsenalCount: 0,
      hasMotionProfile: false,
      motionAccountCount: 0,
      motionOppCount: 0,
      createdAt: '2026-06-01T08:00:00Z',
      updatedAt: '2026-06-01T08:00:00Z',
    },
  ],
};

// ── ListIndustries ───────────────────────────────────────────────────────────
// Includes one empty industry (ECOM) so the "empty industries listed after
// non-empty ones" grouping rule in AssetsPage is exercisable.
export const listIndustriesMock: ListIndustriesOutput = {
  industries: [
    { industry: 'FSI', industryType: 'VERTICAL', solutionMapUrl: 'https://quip/fsi', assetCount: 2, activeMotionCount: 2 },
    { industry: 'GAME', industryType: 'VERTICAL', solutionMapUrl: undefined, assetCount: 1, activeMotionCount: 0 },
    { industry: 'HCLS', industryType: 'VERTICAL', solutionMapUrl: undefined, assetCount: 1, activeMotionCount: 0 },
    { industry: 'RCH', industryType: 'VERTICAL', solutionMapUrl: undefined, assetCount: 1, activeMotionCount: 0 },
    { industry: 'ECOM', industryType: 'HORIZONTAL', solutionMapUrl: undefined, assetCount: 0, activeMotionCount: 0 },
  ],
};

// ── ListStorylines (M2 "View by Storyline" Menu tree) ─────────────────────────
// id = canonical v4 UUID (matches StorylineId @pattern). Ids align with the
// storylineLinks on listAssetsMock so the Menu tree mounts assets under storylines:
//   1111… FSI 智能理赔叙事  → fsi-claims-auto (linkedAssetCount 1)
//   2222… FSI 增长叙事      → EMPTY (0 assets) — exercises the "(0)" node that
//                            asset-reverse-lookup could never produce
//   3333… GAME LiveOps 叙事 → game-liveops-cn (linkedAssetCount 1)
// hcls-genomics / rch-personalize carry no link → their industries show only
// "Unassigned (1)".
export const listStorylinesMock: ListStorylinesOutput = {
  items: [
    {
      id: '11111111-1111-4111-8111-111111111111',
      name: 'FSI 智能理赔叙事',
      industry: 'FSI',
      description: 'MOCK 保险智能理赔的行业叙事线',
      linkedAssetCount: 1,
      createdAt: '2026-07-01T08:00:00Z',
      updatedAt: '2026-07-20T08:00:00Z',
    },
    {
      id: '22222222-2222-4222-8222-222222222222',
      name: 'FSI 增长叙事',
      industry: 'FSI',
      description: '',
      linkedAssetCount: 0, // EMPTY storyline — renders as "FSI 增长叙事 (0)"
      createdAt: '2026-07-10T08:00:00Z',
      updatedAt: '2026-07-10T08:00:00Z',
    },
    {
      id: '33333333-3333-4333-8333-333333333333',
      name: 'GAME LiveOps 叙事',
      industry: 'GAME',
      description: 'MOCK 游戏 LiveOps 运营叙事',
      linkedAssetCount: 1,
      createdAt: '2026-07-05T08:00:00Z',
      updatedAt: '2026-07-18T08:00:00Z',
    },
  ],
};

// ── GetStoryline (M3 detail page) ─────────────────────────────────────────────
// The FSI 智能理赔叙事 storyline (id 1111…) with its one linked asset. Mock dispatch
// is by command name (not id), so this single fixture answers GetStoryline for ANY
// storylineId — fine for dev preview (the detail page renders name/description/linked
// assets). Real per-id detail comes from the backend GetStoryline handler.
export const getStorylineMock: GetStorylineOutput = {
  storyline: {
    id: '11111111-1111-4111-8111-111111111111',
    name: 'FSI 智能理赔叙事',
    industry: 'FSI',
    description: 'MOCK 保险智能理赔的行业叙事线：从触达到落地的完整销售故事。',
    linkedAssets: [{ assetId: 'fsi-claims-auto', name: 'MOCK 智能理赔自动化资产', maturity: 'SCALING' }],
    createdAt: '2026-07-01T08:00:00Z',
    updatedAt: '2026-07-20T15:29:34Z',
  },
};

// ── GetAsset (fsi-claims-auto, the SCALING/motion-ready one) ──────────────────
// Boundary design (HealthCheck.motionReady = completeness>=4 && hasArd && hasFirstCallDeck):
//   completeness 8, hasArd true, hasFirstCallDeck true → motionReady TRUE.
//   Flip any one to break it — that is the gate test.
export const getAssetMock: GetAssetOutput = {
  asset: {
    assetId: 'fsi-claims-auto',
    industry: 'FSI',
    useCase: '智能理赔自动化',
    audience: '保险公司 CTO',
    ownerIs: 'alice',
    maturity: 'SCALING',
    createdAt: '2025-11-02T08:00:00Z',
    updatedAt: '2026-05-30T08:00:00Z',
    createdBy: 'alice',
    updatedBy: 'alice',
    elements: [
      { elementNumber: 1, status: 'VALIDATED', content: '保险公司 CTO 与理赔运营负责人', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 2, status: 'VALIDATED', content: '理赔周期长、人工审核成本高', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 3, status: 'VALIDATED', content: 'Bedrock + 文档智能抽取', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 4, status: 'VALIDATED', content: '理赔时效缩短 60%', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 5, status: 'VALIDATED', content: '参考架构图 v3', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 6, status: 'VALIDATED', content: 'POC 2 周交付', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 7, status: 'VALIDATED', content: '$1.2M ARR', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
      { elementNumber: 8, status: 'VALIDATED', content: '某头部财险落地案例', updatedAt: '2026-05-01T08:00:00Z', updatedBy: 'alice' },
    ],
    arsenal: [
      { id: 1, type: 'ARD', name: 'ARD - 智能理赔', pathOrUrl: 'https://example/ard', createdAt: '2026-04-01T08:00:00Z', createdBy: 'alice' },
      { id: 2, type: 'FIRST_CALL_DECK', name: 'First Call Deck', pathOrUrl: 'https://example/fcd', createdAt: '2026-04-01T08:00:00Z', createdBy: 'alice' },
      { id: 3, type: 'CASE_STUDY', name: '头部财险案例', pathOrUrl: 'https://example/case', createdAt: '2026-04-10T08:00:00Z', createdBy: 'alice' },
      { id: 4, type: 'DEMO_VIDEO', name: 'Demo 视频', pathOrUrl: 'https://example/demo', createdAt: '2026-04-15T08:00:00Z', createdBy: 'alice' },
    ],
    motionSummary: {
      hasProfile: true,
      waveCount: 3,
      funnel: { targeted: 50, knock: 17, push: 7, live: 44, lost: 27, cold: 5 },
      totalArr: 5_250_000,
    },
    healthCheck: {
      elementCompleteness: 8,
      hasArd: true,
      hasFirstCallDeck: true,
      motionReady: true,
    },
    solutionMapLinks: [
      { solutionMapId: 101, industry: 'FSI', solutionArea: '风险与合规', useCase: '智能理赔', role: 'PRIMARY' },
    ],
  },
};

// ── GetSolutionMap (FSI) ─────────────────────────────────────────────────────
export const getSolutionMapMock: GetSolutionMapOutput = {
  industry: 'FSI',
  industryType: 'VERTICAL',
  solutionMapUrl: 'https://quip/fsi',
  entries: [
    {
      id: 101,
      solutionArea: '风险与合规',
      useCase: '智能理赔',
      status: 'ACTIVE',
      priority: 100,
      linkedAssets: [
        { assetId: 'fsi-claims-auto', useCase: '智能理赔自动化', audience: '保险公司 CTO', maturity: 'SCALING', role: 'PRIMARY' },
      ],
    },
    {
      id: 102,
      solutionArea: '风险与合规',
      useCase: '反欺诈',
      status: 'ACTIVE',
      priority: 90,
      linkedAssets: [
        { assetId: 'fsi-fraud-detection', useCase: '实时反欺诈', audience: '银行风控负责人', maturity: 'PLAYBOOK', role: 'PRIMARY' },
      ],
    },
    {
      id: 103,
      solutionArea: '客户体验',
      useCase: '智能客服',
      status: 'PLANNED',
      priority: 50,
      linkedAssets: [],
    },
  ],
};

// ── GetSolutionMapAll (whole matrix, all industries in one call) ─────────────
// Mirrors the ListIndustries + N×GetSolutionMap fixtures it replaces:
//   - the industry set matches listIndustriesMock (incl. empty ECOM, so the
//     "industry with no UCs" dropdown branch stays exercisable),
//   - FSI's entries ARE getSolutionMapMock.entries (one source of truth for the
//     FSI matrix), so a caller deriving UCs gets the same 智能理赔/反欺诈/智能客服.
export const getSolutionMapAllMock: GetSolutionMapAllOutput = {
  industries: [
    {
      industry: 'FSI',
      industryType: 'VERTICAL',
      solutionMapUrl: 'https://quip/fsi',
      entries: getSolutionMapMock.entries,
    },
    {
      industry: 'GAME',
      industryType: 'VERTICAL',
      solutionMapUrl: undefined,
      entries: [
        { id: 201, solutionArea: '运营增长', useCase: '玩家流失预测', status: 'ACTIVE', priority: 80, linkedAssets: [] },
      ],
    },
    {
      industry: 'HCLS',
      industryType: 'VERTICAL',
      solutionMapUrl: undefined,
      entries: [
        { id: 301, solutionArea: '临床智能', useCase: '医学影像分析', status: 'ACTIVE', priority: 70, linkedAssets: [] },
      ],
    },
    {
      industry: 'RCH',
      industryType: 'VERTICAL',
      solutionMapUrl: undefined,
      entries: [
        { id: 401, solutionArea: '门店运营', useCase: '智能补货', status: 'ACTIVE', priority: 60, linkedAssets: [] },
      ],
    },
    // ECOM has no entries → exercises the "（该行业 Solution Map 为空）" UC dropdown branch.
    { industry: 'ECOM', industryType: 'HORIZONTAL', solutionMapUrl: undefined, entries: [] },
  ],
};

// ── ListTopics (covers empty/single/multiple instance topics + each S/R/O priority) ──
// `notes` carries the priority signal ('S'/'R'/'O'/''); design §6.2.
// `industry='_'` means a cross-industry instance (prototype convention).
export const listTopicsMock: ListTopicsOutput = {
  items: [
    {
      name: 'GenAI Agent 框架',
      description: '基于 Bedrock / Strands / MCP 的 agent 装配通用栈',
      sharedTech: 'Bedrock, Strands, MCP, Tool Use',
      knowledgeBase: 'https://example/kb/agent',
      potential: '统一各行业 PoC 的工程化基线',
      agent: { name: 'AgentBuilder', capabilities: 'tool routing, planning', dataSources: 'KB, S3', mcpTools: 'fs, http, sql' },
      instances: [
        // subTopicId (topic_instances.id) — the LinkTopicAsset key. Real backend
        // populates it on the read path; mock carries it so Topic-mode create works.
        { subTopicId: 1001, topicName: 'GenAI Agent 框架', industry: 'FSI', subTopicName: '客户尽调', notes: 'S' },
        { subTopicId: 1002, topicName: 'GenAI Agent 框架', industry: 'AUTO', subTopicName: '智能座舱', notes: 'R' },
        { subTopicId: 1003, topicName: 'GenAI Agent 框架', industry: 'MEAD', subTopicName: '广告投放变现', notes: 'O' },
      ],
    },
    {
      name: '文档智能抽取',
      description: '基于多模态 LLM 的非结构化文档结构化',
      sharedTech: 'Bedrock Claude, Textract',
      knowledgeBase: '',
      potential: 'FSI / HCLS 的核保、问诊、票据通用底座',
      agent: { name: '', capabilities: '', dataSources: '', mcpTools: '' },
      instances: [
        { subTopicId: 1004, topicName: '文档智能抽取', industry: 'FSI', subTopicName: '保险核保', notes: 'S' },
        { subTopicId: 1005, topicName: '文档智能抽取', industry: 'HCLS', subTopicName: '电子病历抽取', notes: 'S' },
      ],
    },
    {
      name: '客户旅程编排',
      description: '跨渠道触点的旅程编排与归因',
      sharedTech: 'Pinpoint, Personalize',
      knowledgeBase: '',
      potential: '',
      agent: { name: '', capabilities: '', dataSources: '', mcpTools: '' },
      instances: [],
    },
  ],
};

// Echo back a Topic for UpsertTopic so optimistic flows in mock mode don't crash.
export const upsertTopicMock: UpsertTopicOutput = {
  topic: listTopicsMock.items![0],
};

export const upsertTopicInstanceMock: UpsertTopicInstanceOutput = {
  instance: { topicName: 'GenAI Agent 框架', industry: 'FSI', subTopicName: '客户尽调', notes: 'S' },
};

// ── GetUcPipelineDetail (opp pool for FSI / Mock 智能理赔) ─────────────────────
// Boundary design for OppPoolPanel:
//   - stages arrive OUT of canonical order (Committed before Prospect) to prove
//     orderedStages() re-sorts to Prospect→…→Launched.
//   - one opp per freshness bucket: daysSinceActivity 3 (green) / 75 (red) /
//     null (gray) — drives the freshnessColors() day-based thresholds.
//   - one opp per terminal OppStatus: Launched (cyan #0891b2) and Closed Lost
//     (gray #64748b) — proves freshnessColors() uses oppStatus, NOT
//     daysSinceActivity, when the opp is closed (MOCK-OPP-104 is only 12d old
//     but should render cyan-not-green; MOCK-OPP-105 has null activity but
//     should render gray-not-red).
//   - one opp carries activities (relevanceScore 3/1 + isIvt) so the activity
//     timeline + relevance dots + IVT chip render; another has none so the row
//     is non-expandable.
//   - Prospect stage carries a stageInsight so the 📊 insight card is exercised.
export const getUcPipelineDetailMock: GetUcPipelineDetailOutput = {
  industry: 'FSI',
  ucName: 'Mock 智能理赔',
  pool: 'wenxiu',
  totalOpps: 5,
  totalArr: 1_350_000,
  stages: [
    {
      stage: 'Committed',
      count: 1,
      arr: 500_000,
      opps: [
        {
          oppId: 'MOCK-OPP-101',
          oppName: 'Mock 理赔平台扩容',
          accountName: 'Mock Account 财险 A',
          stage: 'Committed',
          totalArr: 500_000,
          daysSinceActivity: 3,
          statusLabel: '⚡ 推进 (3天前)',
          activities: [
            {
              date: '2026-06-10',
              alias: 'alice',
              type: 'meeting',
              subject: 'Mock 架构评审会',
              // Multi-line comments exercise the pre-wrap detail block —
              // rendering here proves the ActivityRow expand path AND that
              // line breaks in SFDC comment text are preserved.
              comments:
                '1. Workload: customer wants to migrate claims-auto to Bedrock\n2. Confirmed architecture: RAG + Kendra + Bedrock\n3. Follow-up: schedule POC workshop next week',
              relevanceScore: 3,
              relevanceAsset: 'fsi-claims-auto',
              isIvt: true,
              scoreReason: 'Comments 明确点名 fsi-claims-auto 方案（Bedrock + Kendra RAG），并推进到 POC workshop 阶段，直接命中 Asset 核心能力',
            },
            { date: '2026-06-01', alias: 'bob', type: 'email', subject: 'Mock 报价跟进', comments: undefined, relevanceScore: 1, relevanceAsset: undefined, isIvt: false, scoreReason: '仅涉及商务报价跟进，未讨论 UC 下任何 Asset 的具体能力，弱匹配' },
          ],
        },
      ],
    },
    {
      stage: 'Prospect',
      count: 2,
      arr: 500_000,
      opps: [
        {
          oppId: 'MOCK-OPP-102',
          oppName: 'Mock 理赔 POC',
          accountName: 'Mock Account 财险 B',
          stage: 'Prospect',
          totalArr: 300_000,
          daysSinceActivity: 75,
          statusLabel: '⏸ 停滞 (75天)',
          activities: [],
        },
        {
          oppId: 'MOCK-OPP-103',
          oppName: 'Mock 理赔咨询',
          accountName: 'Mock Account 财险 E',
          stage: 'Prospect',
          totalArr: 200_000,
          daysSinceActivity: undefined,
          statusLabel: undefined,
          activities: [],
        },
      ],
      stageInsight: {
        oppsTouched: 1,
        oppsTotal: 2,
        penetrationPct: 50,
        relevantActs: 2,
        strongActs: 1,
        ivtActs: 1,
        activeOpps: ['Mock Account 财险 B'],
        coolingOpps: [],
        staleOpps: ['Mock Account 财险 E'],
        freq60d: 1,
      },
    },
    {
      stage: 'Launched',
      count: 1,
      arr: 200_000,
      opps: [
        {
          oppId: 'MOCK-OPP-104',
          oppName: 'Mock 理赔上线',
          accountName: 'Mock Account 财险 F',
          stage: 'Launched',
          totalArr: 200_000,
          // 12 days would be green under the day-based logic — but oppStatus=launched
          // must override to cyan. This is the revert-fails guard for terminal-opp
          // color precedence.
          daysSinceActivity: 12,
          oppStatus: 'launched',
          statusLabel: '✅ 已成单',
          activities: [
            { date: '2026-05-20', alias: 'alice', type: 'call', subject: 'Mock 上线复盘', comments: undefined, relevanceScore: 2, relevanceAsset: 'fsi-claims-auto', isIvt: true, scoreReason: '讨论了 fsi-claims-auto 覆盖的理赔自动化业务场景，虽未点名具体产品但场景吻合' },
          ],
        },
      ],
    },
    {
      stage: 'Closed Lost',
      count: 1,
      arr: 150_000,
      opps: [
        {
          oppId: 'MOCK-OPP-105',
          oppName: 'Mock 理赔丢单',
          accountName: 'Mock Account 财险 G',
          stage: 'Closed Lost',
          totalArr: 150_000,
          // null activity would be gray under the day-based logic — coincidentally
          // the same color family as CLOSED_LOST. That's fine: the point is to
          // exercise the oppStatus branch without needing a divergent color.
          daysSinceActivity: undefined,
          oppStatus: 'closed_lost',
          statusLabel: '❌ 已丢单',
          activities: [],
        },
      ],
    },
  ],
};

// ── GetUcFull (FSI) ───────────────────────────────────────────────────────────
// Scoped to the asset's industry (NewAssetUcArsenal calls GetUcFull({industry})).
// The '智能理赔' UC matches getAssetMock's PRIMARY solutionMapLink
// (industry FSI, useCase 智能理赔), so the New Assets detail's 关联 UC 库存弹药
// block resolves to this node's arsenal. Boundary coverage: IVT items WITH
// wins/links/genai/cn AND a bare one; partner items WITH a type; a UC SFDC tag.
// All ids/urls are synthetic (see the file-header SYNTHETIC note).
export const getUcFullMock: GetUcFullOutput = {
  totalUcs: 2,
  totalArsenal: 4,
  industries: [
    {
      industry: 'FSI',
      areas: [
        {
          solutionArea: '风险与合规',
          ucs: [
            {
              // solutionMapId matches getAssetMock's PRIMARY solutionMapLink
              // (id 101, FSI 智能理赔) so NewAssetUcArsenal can resolve this UC by
              // PK once it switches to solutionMapId matching. Also the link key
              // for UcCatalogTree edit mode's LinkUcAsset / UnlinkUcAsset.
              solutionMapId: 101,
              name: '智能理赔',
              priority: 'strategic',
              status: 'ACTIVE',
              tag: 'FSI_CLAIMS_AUTO',
              arsenal: [
                {
                  assetId: 'fsi-claims-auto',
                  name: 'MOCK 智能理赔自动化资产',
                  origin: 'ivt_incubated',
                  description: '基于 Bedrock 的理赔文档智能抽取 + 自动核赔',
                  year: 2026,
                  genai: true,
                  china: true,
                  sfdcTag: 'IVT-FSI-CLAIMS',
                  wins: '某头部财险',
                  url: 'https://example/highspot/claims',
                  ardUrl: 'https://example/ard/claims',
                  uatUrl: 'https://example/uat/claims',
                  bomUrl: 'https://example/bom/claims',
                },
                {
                  assetId: 'fsi-claims-legacy',
                  name: 'MOCK 理赔规则引擎（legacy）',
                  origin: 'ivt_legacy',
                  genai: false,
                  china: false,
                },
              ],
            },
            {
              // solutionMapId 102 = the 反欺诈 entry in getSolutionMapMock.
              solutionMapId: 102,
              name: '反欺诈',
              priority: 'recommended',
              status: 'ACTIVE',
              tag: '',
              arsenal: [
                {
                  assetId: 'partner-fraud-x',
                  name: 'MOCK Partner 反欺诈方案',
                  origin: 'partner',
                  type: 'ProServe',
                  genai: true,
                  china: false,
                  sfdcTag: 'PARTNER-FRAUD',
                  url: 'https://example/partner/fraud',
                },
                {
                  assetId: 'global-fraud-y',
                  name: 'MOCK Global 反欺诈指南',
                  origin: 'global',
                  type: 'Global Guidance',
                  genai: false,
                  china: false,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

// ── GetPipelineBubbles (unified successor: overview + industry-scoped drill) ──
// Same data as getUcPipelineMock + getUcPipelineLiveMock combined into the new
// envelope (`snapshotDate`, no `nextToken`, `PipelineBubble` = superset shape
// carrying both overview AND live fields). The mock client ignores the input
// filter, so both `GetPipelineBubblesCommand({})` (overview) and
// `GetPipelineBubblesCommand({industry: 'FSI'})` (drill) resolve to this same
// row set — the UI filters client-side by input.industry anyway when rendering.
export const getPipelineBubblesMock: GetPipelineBubblesOutput = {
  snapshotDate: '2026-06-13',
  pool: 'wenxiu',
  // The mock client ignores the input filter, so this row set represents the
  // USE_CASE grain (per (industry, UC) row). Asset View reads a different
  // fixture — see getPipelineBubblesAssetMock below.
  groupBy: 'use_case',
  hasInfluence: true,
  // closedLostCount/openCount are chosen so RP-mode (Realized vs. Potential) hits
  // a spread of Win-Rate bands under mocks — one row per band (no-closed / low /
  // mid / high) — so `VITE_IVT_MOCK=1` preview visibly exercises the new mode.
  items: [
    // FSI — 3 UCs (mirrors getUcPipelineLiveMock's per-UC influence + adds industry).
    { industry: 'FSI', ucName: 'Mock 智能理赔', priority: 'strategic', arsenalCount: 4, oppCount: 15, totalArr: 1_200_000, launchedArr: 300_000, launchedCount: 2, closedLostCount: 3, openCount: 10, influenceDensity: 2.4, influenceDensityOpp: 1.8, influenceDensityArr: 2.1, penetrationPct: 73, relevantActs: 28, ivtActs: 11, strongActs: 6 },
    { industry: 'FSI', ucName: 'Mock 实时反欺诈', priority: 'recommended', arsenalCount: 4, oppCount: 8, totalArr: 1_200_000, launchedArr: 0, launchedCount: 0, closedLostCount: 1, openCount: 7, influenceDensity: 1.1, influenceDensityOpp: 0.9, influenceDensityArr: 1.0, penetrationPct: 38, relevantActs: 9, ivtActs: 3, strongActs: 1 },
    { industry: 'FSI', ucName: 'Mock 智能客服', priority: 'opportunistic', arsenalCount: 1, oppCount: 3, totalArr: 400_000, launchedArr: 0, launchedCount: 0, closedLostCount: 0, openCount: 3, influenceDensity: 0, influenceDensityOpp: 0, influenceDensityArr: 0, penetrationPct: 0, relevantActs: 0, ivtActs: 0, strongActs: 0 },
    // GAME — one UC with data + one noise row proving the totalArr>0 || arsenal>0 filter.
    { industry: 'GAME', ucName: 'Mock LiveOps', priority: 'recommended', arsenalCount: 2, oppCount: 6, totalArr: 900_000, launchedArr: 150_000, launchedCount: 1, closedLostCount: 0, openCount: 5, influenceDensity: 1.6, influenceDensityOpp: 1.5, influenceDensityArr: 1.4, penetrationPct: 50, relevantActs: 10, ivtActs: 4, strongActs: 2 },
    { industry: 'GAME', ucName: 'Mock 空 UC（应被过滤）', priority: 'opportunistic', arsenalCount: 0, oppCount: 0, totalArr: 0, launchedArr: 0, launchedCount: 0, closedLostCount: 0, openCount: 0, influenceDensity: 0, influenceDensityOpp: 0, influenceDensityArr: 0, penetrationPct: 0, relevantActs: 0, ivtActs: 0, strongActs: 0 },
  ],
};

// ── GetPipelineBubbles(groupBy=INDUSTRY) — one row per industry, opps deduped
// per (industry, opp_id) on the backend (see UcPipelineDao.findIndustryBubbles).
// Numbers below simulate what the backend produces after per-opp dedup: FSI's
// 26 opp fixtures at the USE_CASE grain collapse to 24 (assumed 2 cross-UC
// overlaps) so the fixture visibly demonstrates the fix vs a naive UC sum.
export const getPipelineBubblesIndustryMock: GetPipelineBubblesOutput = {
  snapshotDate: '2026-06-13',
  pool: 'wenxiu',
  groupBy: 'industry',
  hasInfluence: true,
  items: [
    { industry: 'FSI', arsenalCount: 9, oppCount: 24, totalArr: 2_800_000, launchedArr: 300_000, launchedCount: 2, closedLostCount: 4, openCount: 18, influenceDensity: 1.5, influenceDensityOpp: 1.4, influenceDensityArr: 1.6, penetrationPct: 46, relevantActs: 37, ivtActs: 14, strongActs: 7 },
    { industry: 'GAME', arsenalCount: 2, oppCount: 6, totalArr: 900_000, launchedArr: 150_000, launchedCount: 1, closedLostCount: 0, openCount: 5, influenceDensity: 1.6, influenceDensityOpp: 1.5, influenceDensityArr: 1.4, penetrationPct: 50, relevantActs: 10, ivtActs: 4, strongActs: 2 },
  ],
};

// ── GetPipelineBubbles(groupBy=ACCOUNT) — same shape as USE_CASE grain but the
// row identity moves to `accountName`, and account-only fields (freshness,
// bestDaysSince, ucCount) are populated. This fixture is byte-equivalent to
// getAccountCrossUcMock's `items` — the migration keeps every visible number
// stable on the Account View bubble chart. AccountInsights is now derived
// client-side (see utils/accountInsights.ts) rather than served on the wire,
// so it's not on this fixture.
export const getPipelineBubblesAccountMock: GetPipelineBubblesOutput = {
  snapshotDate: '2026-06-13',
  pool: 'wenxiu',
  groupBy: 'account',
  hasInfluence: true,
  items: [
    {
      industry: 'FSI',
      accountName: 'MOCK-ACCT 财险 A',
      oppCount: 6,
      ucCount: 2,
      totalArr: 1_400_000,
      launchedArr: 0,
      launchedCount: 0,
      closedLostCount: 2,
      openCount: 4,
      arsenalCount: 0,
      influenceDensity: 0.6,
      influenceDensityOpp: 0.5,
      influenceDensityArr: 0.7,
      penetrationPct: 0,
      relevantActs: 4,
      ivtActs: 1,
      strongActs: 0,
      freshness: 'active',
      bestDaysSince: 5,
    },
    {
      industry: 'FSI',
      accountName: 'MOCK-ACCT 财险 B',
      oppCount: 2,
      ucCount: 1,
      totalArr: 150_000,
      launchedArr: 0,
      launchedCount: 0,
      closedLostCount: 0,
      openCount: 2,
      arsenalCount: 0,
      influenceDensity: 5.4,
      influenceDensityOpp: 3.0,
      influenceDensityArr: 4.8,
      penetrationPct: 0,
      relevantActs: 18,
      ivtActs: 7,
      strongActs: 0,
      freshness: 'cooling',
      bestDaysSince: 42,
    },
    {
      industry: 'FSI',
      accountName: 'MOCK-ACCT 财险 C',
      oppCount: 3,
      ucCount: 1,
      totalArr: 300_000,
      launchedArr: 0,
      launchedCount: 0,
      closedLostCount: 1,
      openCount: 2,
      arsenalCount: 0,
      influenceDensity: 0,
      influenceDensityOpp: 0,
      influenceDensityArr: 0,
      penetrationPct: 0,
      relevantActs: 0,
      ivtActs: 0,
      strongActs: 0,
      freshness: 'stale',
      bestDaysSince: 120,
    },
  ],
};

// ── UC Catalog write ops (merged-asset model) ─────────────────────────────────
// Editing a UC's arsenal = UpdateAsset (card fields) / LinkUcAsset+CreateAsset
// (add) / UnlinkUcAsset (remove) / UpsertUcTagById (UC SFDC tag). All four handlers
// are implemented on the backend now — these mocks exist so the edit UI can be driven
// under VITE_IVT_MOCK without network, not because the server side is missing. All
// synthetic (MOCK-* / the file-header SYNTHETIC note). The UI re-fetches GetUcFull
// after a write, so the echo-back payloads only need to be a well-typed success, not
// stateful.
export const updateAssetMock: UpdateAssetOutput = {
  asset: getAssetMock.asset,
};

export const linkUcAssetMock: LinkUcAssetOutput = {
  link: {
    solutionMapId: 101,
    assetId: 'fsi-claims-auto',
    role: 'PRIMARY',
  },
};

export const upsertUcTagMock: UpsertUcTagByIdOutput = {
  tag: {
    industry: 'FSI',
    ucName: '智能理赔',
    tag: 'IVT-FSI-CLAIMS',
  },
};

export const manageArsenalMock: ManageArsenalOutput = {
  arsenal: [
    { id: 1, type: 'ARD', name: 'MOCK ARD 文档', pathOrUrl: 'https://example/ard/claims', createdAt: '2026-06-01T08:00:00Z', createdBy: 'alice' },
    { id: 2, type: 'HIGHSPOT', name: 'MOCK Highspot 链接', pathOrUrl: 'https://example/highspot/claims', createdAt: '2026-06-01T08:00:00Z', createdBy: 'alice' },
  ],
};

// ── CreateMotion (V14 motion-centric) ────────────────────────────────────────
// Only the create echo is mocked this slice — the Motions list page is an empty
// skeleton that does NOT call ListMotions yet (see MotionsPage), so there is no
// listMotionsMock. The motionId is a synthetic opaque base36 id (MotionId pattern
// ^[a-z0-9]+$); useCases is what the backend would DERIVE from the chosen assets
// (DISTINCT assets.(industry, use_case)) — here a single representative ref so the
// echo is a complete, type-checked Motion.
export const createMotionMock: CreateMotionOutput = {
  motion: {
    motionId: 'mockmotion001',
    name: 'Mock Motion',
    industry: 'FSI',
    assetIds: ['fsi-claims-auto'],
    useCases: [{ industry: 'FSI', ucName: '智能理赔' }],
    status: 'active',
    createdAt: '2026-06-30T08:00:00Z',
    updatedAt: '2026-06-30T08:00:00Z',
    createdBy: 'alice',
    updatedBy: 'alice',
  },
};

// ── ListMotions (V14 motion-centric) ─────────────────────────────────────────
// Boundary design for the list table: one empty Motion (no pipeline → backend
// returns progress "" which the UI renders as "—", 0 ARR, 0 tagged), one with
// pipeline + some tagged, one spanning multiple assets/UCs. Drives the Motions
// list page + revert-fails column tests.
export const listMotionsMock: ListMotionsOutput = {
  motions: [
    {
      motionId: 'mockmotion001',
      name: 'FSI Claims Greenfield',
      industry: 'FSI',
      // Empty funnel → backend emits "" (no members yet); UI shows "—".
      progress: '',
      pipelineTotal: 0,
      totalArr: 300000,
      pipelineTagged: 0,
      assetCount: 1,
      useCaseCount: 1,
      createdAt: '2026-06-20T08:00:00Z',
    },
    {
      motionId: 'mockmotion002',
      name: 'GAME LiveOps Accelerate',
      industry: 'GAME',
      progress: 'Targeted 8 / Knock 3 / Live 1',
      pipelineTotal: 12,
      totalArr: 1250000,
      pipelineTagged: 5,
      assetCount: 2,
      useCaseCount: 2,
      createdAt: '2026-06-22T08:00:00Z',
    },
    {
      motionId: 'mockmotion003',
      name: 'HCLS Genomics Cross-UC',
      industry: 'HCLS',
      progress: 'Targeted 20 / Push 4',
      pipelineTotal: 24,
      totalArr: 980000,
      pipelineTagged: 7,
      assetCount: 3,
      useCaseCount: 3,
      createdAt: '2026-06-25T08:00:00Z',
    },
  ],
};

// ── GetMotionOverview (V14 motion-centric) ───────────────────────────────────
// Boundary design for the detail page:
//   - pipelineTotal > accelerateOpps.length (simulates Greenfield accounts too)
//   - Multiple SFDC stages for donut chart verification
//   - Mix of TAGGED / PENDING status for the Status column
//   - One opp with tags (TAGGED), several without (PENDING)
//   - arr spans from 0 to 300K to test formatting
export const getMotionOverviewMock = {
  motionId: 'mockmotion002',
  name: 'GAME LiveOps Accelerate',
  industry: 'GAME',
  pipelineTotal: 48,
  totalArr: 2450000,
  taggedPipeline: { oppCount: 12, arr: 1800000 },
  pendingTagPipeline: { oppCount: 36, arr: 650000 },
  oppStageCounts: { targeted: 10, knock: 8, push: 12, live: 6, lost: 2, cold: 10 },
  accelerateOpps: [
    {
      id: 'pool_001', opptyId: 'MOCK-OPP-001', stage: 'PUSH',
      ownerAlias: 'liu', waveId: 'w_01', notes: '',
      createdAt: '2026-05-01T00:00:00Z', updatedAt: '2026-06-15T00:00:00Z',
      sfdcAccountId: 'MOCK-ACCT-001', accountName: 'Great Wall',
      oppName: 'GLB-GreatWall-Bedrock [GENAI]', oppStage: 'Prospect',
      arr: 300000, closeDate: '2026-09-30',
      tags: ['GCR-IVT-AUTO-VirtualSimulationValidation&Development'],
      waveName: 'Wave 1', status: 'TAGGED',
    },
    {
      id: 'pool_002', opptyId: 'MOCK-OPP-002', stage: 'TARGETED',
      ownerAlias: 'chen', waveId: 'w_01', notes: '',
      createdAt: '2026-05-15T00:00:00Z', updatedAt: '2026-06-20T00:00:00Z',
      sfdcAccountId: 'MOCK-ACCT-002', accountName: 'PayerMax',
      oppName: 'GLB-PayerMax-SageMaker [ML]', oppStage: 'Biz Validation',
      arr: 180000, closeDate: '2026-11-30', tags: [],
      waveName: 'Wave 1', status: 'PENDING',
    },
    {
      id: 'pool_003', opptyId: 'MOCK-OPP-003', stage: 'KNOCK',
      ownerAlias: 'wang', waveId: 'w_02', notes: '',
      createdAt: '2026-06-01T00:00:00Z', updatedAt: '2026-06-25T00:00:00Z',
      sfdcAccountId: 'MOCK-ACCT-003', accountName: 'SF Express',
      oppName: 'GLB-SFExpress-GameTech [GAME]', oppStage: 'Tech Validation',
      arr: 95000, closeDate: '2026-12-15', tags: [],
      waveName: 'Wave 2', status: 'PENDING',
    },
    {
      id: 'pool_004', opptyId: 'MOCK-OPP-004', stage: 'LIVE',
      ownerAlias: 'zhang', waveId: 'w_01', notes: '',
      createdAt: '2026-04-20T00:00:00Z', updatedAt: '2026-06-28T00:00:00Z',
      sfdcAccountId: 'MOCK-ACCT-004', accountName: 'Webull',
      oppName: 'GLB-Webull-LiveOps [GAME]', oppStage: 'Biz Validation',
      arr: 420000, closeDate: '2026-08-15',
      tags: ['GCR-IVT-GAME-LiveOps'],
      waveName: 'Wave 1', status: 'TAGGED',
    },
    {
      id: 'pool_005', opptyId: 'MOCK-OPP-005', stage: 'TARGETED',
      ownerAlias: 'li', waveId: 'w_02', notes: '',
      createdAt: '2026-06-10T00:00:00Z', updatedAt: '2026-06-30T00:00:00Z',
      sfdcAccountId: 'MOCK-ACCT-005', accountName: 'Garena',
      oppName: 'GLB-Garena-CloudGaming [GAME]', oppStage: 'Qualified',
      arr: 250000, closeDate: '2027-01-30', tags: [],
      waveName: 'Wave 2', status: 'PENDING',
    },
    {
      id: 'pool_006', opptyId: 'MOCK-OPP-006', stage: 'PUSH',
      ownerAlias: 'liu', waveId: 'w_01', notes: '',
      createdAt: '2026-05-05T00:00:00Z', updatedAt: '2026-06-22T00:00:00Z',
      sfdcAccountId: 'MOCK-ACCT-006', accountName: 'miHoYo',
      oppName: 'GLB-miHoYo-Infra [GAME]', oppStage: 'Launched',
      arr: 520000, closeDate: '2026-07-31',
      tags: ['GCR-IVT-GAME-Infra'],
      waveName: 'Wave 1', status: 'TAGGED',
    },
  ],
};

// ── ListTaggedPipelines (Motions page tab 2 "Pipeline List") ──────────────────
// Tag-anchored Opp detail + SFDC stage counts (Information bar). Every row is a
// tagged Opp (matchedTags non-empty). activityCount comes back 0 from the backend
// for opps with no cached activity. One Opp anchored by 2 Motions is a SINGLE row
// carrying both in motions[] (one-row-per-Opp model). stageCounts are backend-
// aggregated (not derived from items). Rows are PipelineListItem — the data source
// for the live ListTaggedPipelines + GetPipelineFilterOptions mocks.
export const listTaggedPipelineMock: ListTaggedPipelinesOutput = {
  // Information KPIs. Kept CONSISTENT with the `items` below so the mock view
  // isn't self-contradictory: the 3 rows are 1× Qualified (MOCK-OPP-2) + 2× Tech
  // Validation (MOCK-OPP-1 under two Motions). Backend counts per Opp×Motion row
  // (no oppty dedup — see MotionPipelineDao.countByStage), so the same Opp under
  // 2 Motions contributes 2 to Tech Validation, matching the 2 rows here.
  stageCounts: {
    prospect: 0,
    qualified: 1,
    techValidation: 2,
    bizValidation: 0,
    committed: 0,
    launched: 0,
    others: 0,
  },
  items: [
    // NOTE on UC names: pipeline rows derive Use Cases from the linked ASSETS
    // (motion_asset_link → assets.use_case), so these match the assetIds' own
    // useCase fields in listAssetsMock (e.g. game-liveops-cn → 'LiveOps 实时运营').
    // They intentionally DIFFER from the SolutionMap CATALOG UCs in
    // getSolutionMapAllMock (e.g. GAME → '玩家流失预测') — that catalog feeds the
    // Use Case FILTER dropdown, a different axis. Not a mock inconsistency.
    // MANY everything: the owning Motion links 4 assets → 4 UCs; Opp has 3 tags.
    // Deliberately > the OverflowChips visibleCount (2) so the "+N" fold shows in
    // the Assets / Use Cases / Tags columns.
    // ONE row per Opp (one-row-per-Opp model). MOCK-OPP-1 is anchored by TWO
    // Motions → a single row carrying both in `motions[]` (each MotionRef has its
    // own name + ownerAlias), plus the union-dedup asset/UC/topic scope across
    // both. MANY everything: 4 assets / 4 UCs / 3 tags (> OverflowChips visibleCount
    // 2) so the "+N" fold shows; 2 motions → the Motion column also folds.
    {
      accountName: 'Mock Account A',
      accountId: 'MOCK-ACCT-A',
      opptyId: 'MOCK-OPP-1',
      oppName: 'Mock Opp — Claims Platform',
      motions: [
        { motionId: 'mockmotion002', motionName: 'GAME LiveOps Accelerate', ownerAlias: 'alice' },
        { motionId: 'mockmotion003', motionName: 'HCLS Genomics Cross-UC', ownerAlias: 'carol' },
      ],
      // Union-dedup across BOTH anchoring Motions (GAME's 4 + HCLS's 1 = 5 assets).
      assetIds: [
        'game-liveops-cn',
        'fsi-claims-auto',
        'fsi-fraud-detection',
        'rch-personalize',
        'hcls-genomics',
      ],
      useCases: [
        { industry: 'GAME', ucName: 'LiveOps 实时运营' },
        { industry: 'FSI', ucName: '智能理赔自动化' },
        { industry: 'FSI', ucName: '实时反欺诈' },
        { industry: 'RCH', ucName: '零售个性化推荐' },
        { industry: 'HCLS', ucName: '基因组学数据平台' },
      ],
      arr: 1250000,
      oppOwner: 'sfdc-owner-1', // SFDC opp owner; shown in Owner column
      pipelineScore: 3, // strong-relevance (green badge) in the Score column
      sfdcStage: 'Tech Validation',
      // matchedTags = the tag-anchored field the Tags column reads (registered tags
      // only); 3 → exercises the OverflowChips "+N" fold.
      matchedTags: [
        'GCR-IVT-AUTO-VirtualSimulationValidation&Development',
        'GCR-IVT-GAME-Infra',
        'GCR-IVT-FSI-Fraud',
      ],
      // Topic-anchored links (chip = "父 · 子"); 3 topics → exercises the "+N" fold.
      topicLinks: [
        { subTopicId: 2001, topicName: 'AI Coding', subTopicName: 'AIDLC', industry: undefined },
        { subTopicId: 2002, topicName: 'GenAI Agent 框架', subTopicName: '客户尽调', industry: 'FSI' },
        { subTopicId: 2003, topicName: '文档智能抽取', subTopicName: '保险核保', industry: 'FSI' },
      ],
      activityCount: 3,
      ivtActivityCount: 2, // 2 of the 3 activities are IVT-owned → "3 次活动（2 次 IVT）"
    },
    // EDGE: a SINGLE-Motion Opp with 0 assets → 0 UCs; single tag; 0 activities; no
    // topics; and NO oppOwner (empty SFDC owner → Owner column shows "—").
    {
      accountName: 'Mock Account B',
      accountId: 'MOCK-ACCT-B',
      opptyId: 'MOCK-OPP-2',
      oppName: 'Mock Opp — Genomics Pipeline',
      motions: [{ motionId: 'mockmotion001', motionName: 'FSI Claims Greenfield', ownerAlias: 'bob' }],
      assetIds: [],
      useCases: [],
      arr: 980000,
      pipelineScore: 0, // score 0 → shares score 1's red badge (product rule)
      sfdcStage: 'Qualified',
      matchedTags: ['GCR-IVT-HCLS'],
      topicLinks: [],
      activityCount: 0,
    },
  ],
};

// ── GetOppActivities (Pipeline row "N 次活动" click → activity timeline) ───────
// One opp's activity detail, newest-first. date is day-precision only (backend
// activity_cache.activity_date is SQL DATE — no time-of-day; see §gap). The mock
// mirrors that: date-only strings.
// NOTE: the mock client dispatches by command name only (mockClient.ts), so this
// singleton answers GetOppActivities for ANY opptyId. That's fine because the UI
// only opens the popup for rows with activityCount > 0, and the only such row
// (MOCK-OPP-1) has 3 activities — matching the 3 entries here. MOCK-OPP-2's
// activityCount is 0, so its "0 次活动" is a non-clickable span (never fetches).
export const getOppActivitiesMock: GetOppActivitiesOutput = {
  activities: [
    {
      activityId: 'MOCK-ACT-1',
      date: '2026-06-05',
      activityType: 'Meeting',
      bdActivityType: 'SA - ARCH - Architecture Review',
      subject: '[Architecture] ABC - 双周例会',
      comments: 'Reviewed the target architecture and next-step action items with the customer team.',
      ownerAlias: 'alice',
      status: 'Completed',
      isIvt: true, // owner in ivt_user → [IVT] tag
    },
    {
      activityId: 'MOCK-ACT-2',
      date: '2026-06-03',
      activityType: 'Call',
      bdActivityType: 'SA - Discovery',
      subject: '[Discovery] 需求澄清电话',
      comments: 'Clarified the LiveOps use case scope and success criteria.',
      ownerAlias: 'alice',
      status: 'Completed',
      isIvt: true,
    },
    {
      activityId: 'MOCK-ACT-3',
      date: '2026-06-01',
      activityType: 'Email',
      bdActivityType: 'BD - Follow-up',
      subject: '[Follow-up] 会议纪要与后续',
      comments: 'Sent the meeting recap and proposed the next working session.',
      ownerAlias: 'bob',
      status: 'Open',
      isIvt: false, // non-IVT owner → NO tag
    },
  ],
};

// ── Asset KB file management (A-line) ────────────────────────────────────────
// GetKbTree echo — the owner-generic read path (replaced the retired List*KbFiles).
// status stays at `uploaded` this iteration (B-line ingest → ready/failed not
// attached); all synthetic (MOCK-*), no real data. folders empty (backend seeds them
// in CR-3); no overview yet.
export const getKbTreeMock: GetKbTreeOutput = {
  folders: [],
  entries: [
    {
      fileId: 'MOCK-KB-FILE-1',
      entryType: 'file' as never,
      sourceType: 'USER_UPLOAD' as never,
      displayName: '嵌入式AI开发指南.pdf',
      contentType: 'application/pdf',
      fileSizeBytes: 2_412_000,
      status: 'uploaded' as never,
      uploadedBy: 'alice',
      uploadedAt: '2026-07-08T02:15:00Z',
    },
    {
      fileId: 'MOCK-KB-FILE-2',
      entryType: 'file' as never,
      sourceType: 'USER_UPLOAD' as never,
      displayName: 'first-call-deck.pdf',
      contentType: 'application/pdf',
      fileSizeBytes: 8_130_000,
      status: 'uploaded' as never,
      uploadedBy: 'bob',
      uploadedAt: '2026-07-07T09:40:00Z',
    },
  ],
};

// RequestKbFileUploadUrl echo — a fake presigned PUT URL + fileId. In mock mode the
// browser PUT to this URL won't really hit S3, but the two-step flow completes.
export const requestKbUploadUrlMock: RequestKbFileUploadUrlOutput = {
  fileId: 'MOCK-KB-FILE-NEW',
  uploadUrl: 'https://mock-kb-bucket.example.com/upload?mock=1',
  expiresInSeconds: 900,
};

// RequestKbOverviewUploadUrl echo — deterministic Overview fileId + presigned URL.
// getKbTreeMock.overview is absent, so mock mode exercises the first-save path.
export const requestKbOverviewUploadUrlMock: RequestKbOverviewUploadUrlOutput = {
  fileId: 'MOCK-KB-OVERVIEW',
  uploadUrl: 'https://mock-kb-bucket.example.com/overview-upload?mock=1',
  expiresInSeconds: 900,
};

// ConfirmKbFileUpload echo — the confirmed entry (owner-generic ops return {entry:
// KbEntry}, not the old {file: KbFile}).
export const confirmKbUploadCompleteMock: ConfirmKbFileUploadOutput = {
  entry: {
    fileId: 'MOCK-KB-FILE-NEW',
    entryType: 'file' as never,
    sourceType: 'USER_UPLOAD' as never,
    displayName: 'mock-upload.pdf',
    contentType: 'application/pdf',
    fileSizeBytes: 1_024_000,
    status: 'uploaded' as never,
    uploadedBy: 'fanhuang',
    uploadedAt: '2026-07-09T00:00:00Z',
  },
};

// ── GetAssetAssessment (fsi-claims-auto) ──────────────────────────────────────
// Fully-scored assessment — exercises the Details card's 已打分 render path for
// the assessment-dependent cells (8-Element Quality, Assessment Score,
// Dual-axis Classification). Unscored assets return no fixture; the mock
// client can also fall back to `{ assessment: undefined }` for those.
export const getAssetAssessmentMock: GetAssetAssessmentOutput = {
  assessment: {
    assetId: 'fsi-claims-auto',
    eightElementQuality: 87.5,
    elementScores: {
      painpoint: 5,
      audience: 4,
      leverage: 5,
      scenario: 3,
      metric: 4,
      reference: 5,
      competitive: 4,
      objection: 5,
    },
    elementScoredAt: '2026-07-14T09:30:00Z',
    scoreX: 4.5,
    scoreY: 3.0,
    weighingReportFileId: '11111111-2222-3333-4444-555555555555',
    weighedAt: '2026-07-14T10:00:00Z',
    industryRank: { rank: 3, total: 20 },
  },
};

/* ── Admin configuration (sync-scope) ──────────────────────────────────────
   Reproduces what V68 seeds, so mock mode shows the same starting state a freshly
   migrated stage would: three columns OR'd, RELATIVE/0 (= this year), the one tag
   prefix, and V63's identity industry mapping. Editing from here is what a PM would
   actually be doing on day one.

   Industry list matches listIndustriesMock's codes so the two pages agree. */
export const getConfigurationMock: GetConfigurationOutput = {
  configuration: {
    syncScope: {
      dateConditions: Object.fromEntries(
        ['close_date', 'close_staged_date', 'created_date'].map((c) => [
          c,
          {
            operation: 'and',
            tokens: [
              { propertyKey: 'from', operator: 'relative', value: '0' },
              { propertyKey: 'to', operator: 'relative', value: '1' },
            ],
          },
        ]),
      ),
      recordTypes: ['Utility'],
      tagPrefixes: ['GCR-IVT-'],
      industries: [
        { industry: 'FSI', sfdcShLevel: 'sh_l4', nodes: [{ level: 'sh_l4', name: 'FSI' }], updatedBy: 'fuxin' },
        { industry: 'GAME', sfdcShLevel: 'sh_l4', nodes: [{ level: 'sh_l4', name: 'GAME' }] },
        { industry: 'HCLS', sfdcShLevel: 'sh_l4', nodes: [{ level: 'sh_l4', name: 'HCLS' }] },
        { industry: 'RCH', sfdcShLevel: 'sh_l4', nodes: [{ level: 'sh_l4', name: 'RCH' }] },
        // An industry with nothing mapped — V63's default, and a legitimate
        // "matches zero pipeline" state rather than a row to hide.
        { industry: 'ECOM', sfdcShLevel: 'sh_l5', nodes: [] },
      ],
    },
  },
  revision: '7',
  updatedAt: new Date('2026-09-01T03:20:00Z'),
  updatedBy: 'fuxin',
};

/* The write echoes the request back in the real service (it returns the full resource
   so the page needs no follow-up read). The mock cannot see the request, so it returns
   the same payload with the revision advanced — enough to verify the page clears its
   dirty state and re-bases the diff after a save. */
/* ── Industry access (Access tab) ──────────────────────────────────────────
   Same codes as the configuration mock. ECOM has nobody granted, and FSI has more
   principals than a collapsed row shows. MockIvtHubClient keeps its own copy and applies
   create / delete to it; an alias outside grantRosterMock is rejected, standing in for the
   service's InvalidInputException. */
const grantee = (alias: string, day: number) => ({
  alias,
  grantedBy: 'fuxin',
  grantedAt: new Date(`2026-10-${String(day).padStart(2, '0')}T02:00:00Z`),
});
export const industryGrantsMock: IndustryGrants[] = [
  { industry: 'ECOM', displayName: 'ECOM', grantees: [] },
  {
    industry: 'FSI',
    displayName: 'FSI',
    grantees: ['alice', 'bob', 'carol', 'dave', 'erin', 'frank'].map((a, i) => grantee(a, i + 1)),
  },
  { industry: 'GAME', displayName: 'GAME', grantees: [grantee('alice', 3), grantee('grace', 4)] },
  { industry: 'HCLS', displayName: 'HCLS', grantees: [grantee('heidi', 2)] },
  { industry: 'RCH', displayName: 'RCH', grantees: [grantee('bob', 5)] },
];
export const grantRosterMock: readonly string[] = [
  'alice', 'bob', 'carol', 'dave', 'erin', 'frank', 'grace', 'heidi', 'ivan', 'judy', 'mallory',
];

export const updateConfigurationMock: UpdateConfigurationOutput = {
  configuration: getConfigurationMock.configuration,
  revision: '8',
  updatedAt: new Date('2026-09-03T09:00:00Z'),
  updatedBy: 'fuxin',
};

// ── ListAccountPipeline (STO Homepage, lighthouse=true) ───────────────────────
// accountId is the 18-character SFDC form (exactly 18, not 15-or-18), synthetic.
//
// Boundary design, one rule per element:
//   · `coverage` is the WHOLE request scope, not this page and NOT the sum of the
//     rows below — accountCount exceeds the three groups here, and neither bucket is
//     reachable by adding the group headers up. The figures are chosen so naive
//     addition cannot accidentally land on them. This fixture is the LAST page (no
//     nextToken), which is also why the zero account is on it: page order is
//     account ARR descending, so zero accounts sort last.
//   · a group's header bucket count DOES equal its list length — header and list are
//     built off one shared selection with no cap and no extra predicate, so a fixture
//     where they diverged would be teaching a contract the backend does not have.
//     What breaks addition INSIDE a group is ARR: a null-arr opportunity counts
//     toward oppCount and contributes nothing to arr (MOCK-OPP-4104).
//   · MOCKACCTLIGHT00002 has NO accountName — a flagged account can be absent from
//     account_cache, and surfacing that is the point, not an error.
//   · MOCKACCTLIGHT00003 reads all zeros with empty lists — the flagship customer
//     with no pipeline. Only the lighthouse block can produce this; the reclaim
//     block never returns an account without pipeline.
//   · MOCK-OPP-4101 carries a prevStage, MOCK-OPP-4102 carries stageChangedDate but
//     NO prevStage. Absence of prevStage is the only sound test for "never moved" —
//     the source sets the date even on a row that never changed.
//   · MOCK-OPP-4103 is untagged with several suggestions, one already dismissed.
//     Several on one opportunity is normal, and `suggestions` is empty on the tagged
//     side. Every untagged row keeps at least one suggestion at or above the default
//     score floor of 3, because the floor selects OPPORTUNITIES: a row with nothing
//     qualifying is absent from the block and from its counts entirely.
//   · MOCK-OPP-4101 carries two taggedAssets, one without a name; MOCK-OPP-4102 is
//     tagged with NONE. `opp_has_ivt_tag` is off the tag vocabulary directly, so a
//     tag matching no asset the use cases link leaves the list empty on a tagged row —
//     the tagged/untagged split must keep reading that flag, never this emptiness.
export const listAccountPipelineMock: ListAccountPipelineOutput = {
  coverage: {
    accountCount: 14,
    tagged: { oppCount: 19, arr: 21_400_000 },
    untagged: { oppCount: 8, arr: 6_150_000 },
  },
  accounts: [
    {
      account: {
        accountId: 'MOCKACCTLIGHT00001',
        accountName: 'MOCK Account 财险 A',
        isBigBet: true,
        tagged: { oppCount: 2, arr: 4_600_000 },
        // 900_000 over TWO opportunities — MOCK-OPP-4104 has no arr.
        untagged: { oppCount: 2, arr: 900_000 },
      },
      tagged: [
        {
          opptyId: 'MOCK-OPP-4101',
          oppName: 'MOCK 理赔平台二期',
          arr: 3_100_000,
          sfdcStage: 'Tech Validation',
          prevStage: 'Qualified', // moved → the 迁移 badge renders
          stageChangedDate: '2026-09-12',
          createdDate: '2026-04-02',
          keyFieldsLastModifiedDate: '2026-09-20',
          hasIvtActivity: true,
          lastActivityDate: '2026-09-18',
          taggedAssets: [
            { assetId: 'fsi-claims-auto', assetName: 'MOCK 智能理赔自动化资产' },
            { assetId: 'fsi-fraud-detection', assetName: undefined },
          ],
          suggestions: [], // always empty on the tagged side
        },
        {
          opptyId: 'MOCK-OPP-4102',
          oppName: 'MOCK 核保智能化',
          arr: 1_500_000,
          sfdcStage: 'Qualified',
          // NO prevStage, yet stageChangedDate is set — never moved. Reading the date
          // instead of prevStage's absence would badge this row wrongly.
          stageChangedDate: '2026-05-04',
          createdDate: '2026-05-04',
          keyFieldsLastModifiedDate: '2026-06-01',
          hasIvtActivity: false,
          taggedAssets: [], // tagged, yet nothing resolves — see the header
          suggestions: [],
        },
      ],
      untagged: [
        {
          opptyId: 'MOCK-OPP-4103',
          oppName: 'MOCK 智能客服坐席',
          arr: 900_000,
          sfdcStage: 'Business Validation',
          createdDate: '2026-03-18',
          keyFieldsLastModifiedDate: '2026-08-30',
          // IVT worked this through activities but there is no tag to show for it —
          // the "activity only" case.
          hasIvtActivity: true,
          lastActivityDate: '2026-08-30',
          taggedAssets: [], // always empty on the untagged side
          suggestions: [
            { assetId: 'fsi-claims-auto', assetName: 'MOCK 智能理赔自动化资产', relevanceScore: 4, isDismissed: false },
            { assetId: 'fsi-fraud-detection', assetName: undefined, relevanceScore: 3, isDismissed: false },
            // Dismissed recommendations still come back, so the row can show the undo.
            { assetId: 'rch-personalize', assetName: 'MOCK 零售个性化推荐', relevanceScore: 3, isDismissed: true },
          ],
        },
        {
          // No arr: opportunity_cache.arr has no NOT NULL. It still counts toward the
          // header's oppCount of 2 while adding nothing to its 900_000.
          opptyId: 'MOCK-OPP-4104',
          oppName: undefined,
          arr: undefined,
          sfdcStage: 'Prospect',
          createdDate: '2026-09-01',
          hasIvtActivity: false,
          taggedAssets: [],
          suggestions: [
            { assetId: 'fsi-claims-auto', assetName: 'MOCK 智能理赔自动化资产', relevanceScore: 3, isDismissed: false },
          ],
        },
      ],
    },
    {
      account: {
        accountId: 'MOCKACCTLIGHT00002',
        // Absent, not empty: flagged but not mirrored in account_cache. Clients fall
        // back to showing the id.
        accountName: undefined,
        isBigBet: false,
        tagged: { oppCount: 0, arr: 0 },
        untagged: { oppCount: 1, arr: 1_250_000 },
      },
      tagged: [],
      untagged: [
        {
          opptyId: 'MOCK-OPP-4201',
          oppName: 'MOCK 风控数据底座',
          arr: 1_250_000,
          sfdcStage: 'Prospect',
          createdDate: '2026-07-02',
          hasIvtActivity: false,
          lastActivityDate: '2026-07-11',
          taggedAssets: [],
          suggestions: [
            { assetId: 'fsi-fraud-detection', assetName: 'MOCK 实时反欺诈', relevanceScore: 4, isDismissed: false },
          ],
        },
      ],
    },
    {
      // Zero buckets are an answer, not an absence — and not an error to hide.
      account: {
        accountId: 'MOCKACCTLIGHT00003',
        accountName: 'MOCK Account 旗舰 C',
        isBigBet: true,
        tagged: { oppCount: 0, arr: 0 },
        untagged: { oppCount: 0, arr: 0 },
      },
      tagged: [],
      untagged: [],
    },
  ],
};

// ── ListAssetOpportunities (one bucket of one asset, in full) ─────────────────
// The rows behind fsi-claims-auto's OPEN cell. An opportunity can appear under
// several assets, so these rows divide nothing — totals come from the flow op.
//
// Order is ARR descending, NULLS LAST, tie-broken by opptyId — the order the
// per-bucket preview is a PREFIX of, which is why the preview's first entries and
// the expanded list's first entries agree.
//
// systemCloseDate is absent on every row on purpose: it is set only on the closed
// buckets, and this fixture is an open one.
export const listAssetOpportunitiesMock: ListAssetOpportunitiesOutput = {
  items: [
    {
      opptyId: 'MOCK-OPP-4101',
      oppName: 'MOCK 理赔平台二期',
      accountId: 'MOCKACCTLIGHT00001',
      accountName: 'MOCK Account 财险 A',
      arr: 3_100_000,
      sfdcStage: 'Tech Validation',
      createdDate: '2026-04-02',
    },
    {
      opptyId: 'MOCK-OPP-4201',
      oppName: 'MOCK 风控数据底座',
      accountId: 'MOCKACCTLIGHT00002',
      // Same absent name as in listAccountPipelineMock: the account is flagged but
      // not mirrored.
      accountName: undefined,
      arr: 1_250_000,
      sfdcStage: 'Prospect',
      createdDate: '2026-07-02',
    },
    {
      // opptyId is the ONLY required member. This row is the everything-else-null case
      // the source admits, and it sorts last because null ARR sorts last.
      opptyId: 'MOCK-OPP-4302',
      oppName: undefined,
      accountId: undefined,
      accountName: undefined,
      arr: undefined,
      sfdcStage: undefined,
      createdDate: undefined,
    },
  ],
  // Opaque. It really is base64url of a keyset position plus a fingerprint of the
  // scope, so the frontend MUST pass it back verbatim and MUST drop it when any
  // filter changes — a token minted under one scope is rejected with a 400, not
  // silently re-based. This one's fingerprint is synthetic, and the mock dispatches
  // on command name alone, so paging is inert here: a "load more" re-serves page 1.
  nextToken: 'MzEwMDAwMC4wMB9NT0NLLU9QUC00MTAxHzE3NzQzMDAyNTE',
};

// ── ListAccountFlags (the + Add Lighthouse Account picker / roster) ───────────
// Ordered by accountId, the table's primary key, so the order is total and stable.
// Every row carries ALL flags, not just the filtered one, so a row can render its
// badges without a second call.
//
// The BIG_BET-only row is here because omitting `flag` means "every account carrying
// ANY flag" — so a BIG_BET account appears in an unfiltered read even though the
// lighthouse page will not show it. The converse is why there is no all-false row:
// such a row survives a DeleteAccountFlag but is NOT returned by an unfiltered read.
//
// MOCKACCTLIGHT00002 again has no accountName, and no createdBy/createdAt either —
// migration-seeded rows have no alias to name, and the pair is traceability rather
// than authorization, so nothing reads it to decide access.
export const listAccountFlagsMock: ListAccountFlagsOutput = {
  items: [
    {
      accountId: 'MOCKACCTBIGBET0001',
      accountName: 'MOCK Account 大单 D',
      isLighthouse: false,
      isBigBet: true,
      createdBy: 'carol',
      createdAt: new Date('2026-08-14T02:00:00Z'),
    },
    {
      accountId: 'MOCKACCTLIGHT00001',
      accountName: 'MOCK Account 财险 A',
      isLighthouse: true,
      // Both flags on one account. createdBy is at the ROW's grain, not the flag's,
      // so it names whoever caused the FIRST one.
      isBigBet: true,
      createdBy: 'alice',
      createdAt: new Date('2026-09-02T06:00:00Z'),
    },
    {
      accountId: 'MOCKACCTLIGHT00002',
      accountName: undefined,
      isLighthouse: true,
      isBigBet: false,
      createdBy: undefined,
      createdAt: undefined,
    },
    {
      accountId: 'MOCKACCTLIGHT00003',
      accountName: 'MOCK Account 旗舰 C',
      isLighthouse: true,
      isBigBet: true,
      createdBy: 'bob',
      createdAt: new Date('2026-09-20T01:30:00Z'),
    },
  ],
};

// ── STO write ops ─────────────────────────────────────────────────────────────
// All four return an empty body; the page re-reads its list afterwards, so there is
// nothing to echo. They are typed rather than written as bare `{}` at the dispatch
// site so that the day one of these outputs gains a member, these stop compiling
// instead of the mock quietly under-serving it.
export const putOppAssetDismissalMock: PutOppAssetDismissalOutput = {};
export const deleteOppAssetDismissalMock: DeleteOppAssetDismissalOutput = {};
export const putAccountFlagMock: PutAccountFlagOutput = {};
export const deleteAccountFlagMock: DeleteAccountFlagOutput = {};

// ── SearchAccounts (Add Lighthouse Account dialog) ───────────────────────────
// NOT an output fixture: MockIvtHubClient.searchAccounts filters, sorts and pages this
// pool the way the service does, so each row carries the one thing the wire does not —
// the industry its OWN hierarchy resolves to, which is what `industries` narrows on.
// The mock strips it before answering.
//
// Boundaries encoded:
//   · the three MOCKACCTLIGHT* accounts are the ones listAccountPipelineMock shows in
//     the lighthouse block, so here they come back `isLighthouse: true` (greyed out);
//   · MOCKACCTLIGHT00002 has no name in account_cache there, and has none here either;
//   · MOCKACCTNONAME0001 has neither name — it sorts LAST, and labels by its id;
//   · 55 candidates, so the default page of 50 leaves a second page to scroll to;
//   · every candidate's Chinese name differs from its English one, so a Chinese search
//     that finds a row can only have matched on `accountAkaName`.
export type SearchAccountsPoolRow = AccountCandidate & { industry: string };

const CANDIDATE_INDUSTRIES = ['FSI', 'HCLS', 'GAME'] as const;

export const searchAccountsPool: readonly SearchAccountsPoolRow[] = [
  {
    accountId: 'MOCKACCTLIGHT00001',
    accountName: 'MOCK Account 财险 A',
    accountAkaName: 'MOCK 财险甲',
    isLighthouse: true,
    industry: 'FSI',
  },
  { accountId: 'MOCKACCTLIGHT00002', accountName: undefined, isLighthouse: true, industry: 'FSI' },
  {
    accountId: 'MOCKACCTLIGHT00003',
    accountName: 'MOCK Account 旗舰 C',
    accountAkaName: 'MOCK 旗舰丙',
    isLighthouse: true,
    industry: 'HCLS',
  },
  ...Array.from({ length: 55 }, (_, i): SearchAccountsPoolRow => {
    const n = String(i + 1).padStart(2, '0');
    return {
      accountId: `MOCKACCTCAND0000${n}`,
      accountName: `MOCK Candidate ${n}`,
      accountAkaName: `MOCK 候选客户${n}`,
      isLighthouse: false,
      industry: CANDIDATE_INDUSTRIES[i % CANDIDATE_INDUSTRIES.length],
    };
  }),
  { accountId: 'MOCKACCTNONAME0001', isLighthouse: false, industry: 'HCLS' },
];

// ── ListRadarDirections (DAT-9115, R17) ──────────────────────────────────────
// Three fixtures covering the shapes R17 renders. The mock dispatch picks one
// based on the `industry` request param so the dev-server can hop between
// states without re-seeding (see MockIvtHubClient).
//
// Boundary design:
//   HCLS (多主题, Figma A)   — two HEALTHCARE standard topics plus the general
//                             bucket. Three cards: a BOTH-evidence card
//                             (pipeline chips + news items + 泰格医药 suggested-
//                             org); a NEWS_ONLY card (chips=0, one news item,
//                             one suggested org); and a PIPELINE_ONLY card
//                             (chips populated, empty news, empty orgs).
//   FSI  (仅通用, Figma C)   — ONLY the general bucket, with three directions —
//                             exercises the ordering rule (general renders last
//                             even when it is the only group).
//   GAME (空态, Figma D)     — zero directions, emptyNewsState=NO_NEWS_FOR_INDUSTRY
//                             (the empty banner path).
//
// All values are synthetic — every account chip, suggested-org name and ARR
// is a MOCK-prefixed invention. Nothing here is a real SFDC id, real account
// name, or real customer revenue (file-level invariant, see the module
// header above).
const RADAR_HCLS: ListRadarDirectionsOutput = {
  directions: [
    {
      directionId: 'mockdirhcls001',
      industry: 'HCLS',
      topicId: 'mocktopichcls1',
      topicTitle: '创新药研发主题',
      name: 'AI 驱动的临床试验方案设计',
      rationale:
        '近 90 天 5 家不同 Account 独立出现同一诉求:用 LLM 从历史试验数据生成 protocol 草案 + 患者入组标准。5 家中 3 家已在 Technical Validation 阶段;新闻侧两条 Google News 命中同一关键词。',
      status: RadarDirectionStatus.CANDIDATE,
      evidenceTier: RadarEvidenceTier.BOTH,
      pipelineEvidence: {
        distinctAccounts: 5,
        oppCount: 5,
        totalArrUsd: 1_340_000,
        technicalValidationAccountCount: 3,
      },
      accountChips: [
        { accountName: 'MOCK Pharma A', totalArrUsd: 420_000 },
        { accountName: 'MOCK Pharma B', totalArrUsd: 360_000 },
        { accountName: 'MOCK Pharma C', totalArrUsd: 280_000 },
        { accountName: 'MOCK Pharma D', totalArrUsd: 180_000 },
        { accountName: 'MOCK Pharma E', totalArrUsd: 100_000 },
      ],
      recentNews: [
        {
          sourceKind: RadarNewsSourceKind.GOOGLE_NEWS_KEYWORD,
          fetchMode: RadarNewsFetchMode.MANUALLY_UPLOADED,
          publishedAt: '2026-07-19',
          url: 'https://example.invalid/news/mock-protocol-llm',
          title: '多家申办方试点大模型生成试验方案草案',
        },
        {
          sourceKind: RadarNewsSourceKind.GOOGLE_NEWS_SITE,
          fetchMode: RadarNewsFetchMode.SYSTEM_FETCHED,
          publishedAt: '2026-06-11',
          url: 'https://example.invalid/news/mock-china-trial',
          title: '中国区临床运营数字化年度规划发布',
        },
      ],
      suggestedOrgs: [
        {
          name: 'MOCK 某国家级药物临床试验机构联盟',
          note: '非 AWS 客户',
          side: RadarSuggestedOrgSide.NAMED_IN_NEWS,
        },
        {
          name: 'MOCK CRO Alpha',
          note: 'AWS 客户 · 本方向 0 笔',
          side: RadarSuggestedOrgSide.NAMED_IN_NEWS,
        },
      ],
      emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    },
    {
      directionId: 'mockdirhcls002',
      industry: 'HCLS',
      topicId: 'mocktopichcls2',
      topicTitle: '器械创新主题',
      name: '手术机器人视觉模型本地部署',
      rationale: '新闻侧 3 条独立报道提及同一需求;管线侧暂无成交。',
      status: RadarDirectionStatus.CANDIDATE,
      evidenceTier: RadarEvidenceTier.NEWS_ONLY,
      pipelineEvidence: {
        distinctAccounts: 0,
        oppCount: 0,
        totalArrUsd: 0,
        technicalValidationAccountCount: 0,
      },
      accountChips: [],
      recentNews: [
        {
          sourceKind: RadarNewsSourceKind.GOOGLE_NEWS_KEYWORD,
          fetchMode: RadarNewsFetchMode.SYSTEM_FETCHED,
          publishedAt: '2026-08-02',
          url: 'https://example.invalid/news/mock-surgical-robot',
          title: '国产手术机器人视觉模型加速本地部署',
        },
        {
          fetchMode: RadarNewsFetchMode.MANUALLY_UPLOADED,
          publishedAt: '2026-07-25',
          title: '器械注册新规下的本地化部署路径(内部摘要,无链接)',
        },
      ],
      suggestedOrgs: [
        {
          name: '某头部国产器械厂商',
          side: RadarSuggestedOrgSide.NAMED_IN_NEWS,
        },
      ],
      emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    },
    {
      directionId: 'mockdirhcls003',
      industry: 'HCLS',
      topicId: 'mocktopicgenhcls',
      topicTitle: '其他 / 待归主题',
      name: '区域医保结算智能稽核',
      rationale: '来自 4 家不同 Account 的近期活动笔记提到同一问题。',
      status: RadarDirectionStatus.CANDIDATE,
      evidenceTier: RadarEvidenceTier.PIPELINE_ONLY,
      pipelineEvidence: {
        distinctAccounts: 4,
        oppCount: 6,
        totalArrUsd: 820_000,
        technicalValidationAccountCount: 1,
      },
      accountChips: [
        { accountName: 'MOCK 省级医保局 A', totalArrUsd: 300_000 },
        { accountName: 'MOCK 省级医保局 B', totalArrUsd: 220_000 },
      ],
      recentNews: [],
      suggestedOrgs: [],
      emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    },
  ],
  summary: {
    potentialDirectionCount: 3,
    bothEvidencePresentCount: 1,
    potentialCustomerContactCount: 3,
  },
};

const RADAR_FSI: ListRadarDirectionsOutput = {
  directions: [
    {
      directionId: 'mockdirfsi001',
      industry: 'FSI',
      topicId: 'mocktopicgenfsi',
      topicTitle: '其他 / 待归主题',
      name: '账户欺诈模式识别',
      rationale: '来自 3 家券商的近 90 天活动笔记里同一诉求出现 7 次。',
      status: RadarDirectionStatus.CANDIDATE,
      evidenceTier: RadarEvidenceTier.PIPELINE_ONLY,
      pipelineEvidence: {
        distinctAccounts: 3,
        oppCount: 4,
        totalArrUsd: 560_000,
        technicalValidationAccountCount: 2,
      },
      accountChips: [
        { accountName: 'MOCK 券商 A', totalArrUsd: 240_000 },
        { accountName: 'MOCK 券商 B', totalArrUsd: 200_000 },
        { accountName: 'MOCK 券商 C', totalArrUsd: 120_000 },
      ],
      recentNews: [],
      suggestedOrgs: [],
      emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    },
    {
      directionId: 'mockdirfsi002',
      industry: 'FSI',
      topicId: 'mocktopicgenfsi',
      topicTitle: '其他 / 待归主题',
      name: '财险理赔 GenAI 预审',
      rationale: '来自 2 家保险公司的近期活动笔记里一致的 GenAI 预审诉求。',
      status: RadarDirectionStatus.CANDIDATE,
      evidenceTier: RadarEvidenceTier.PIPELINE_ONLY,
      pipelineEvidence: {
        distinctAccounts: 2,
        oppCount: 3,
        totalArrUsd: 410_000,
        technicalValidationAccountCount: 0,
      },
      accountChips: [{ accountName: 'MOCK 保险 D', totalArrUsd: 260_000 }],
      recentNews: [],
      suggestedOrgs: [],
      emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    },
  ],
  summary: {
    potentialDirectionCount: 2,
    bothEvidencePresentCount: 0,
    potentialCustomerContactCount: 0,
  },
};

const RADAR_GAME: ListRadarDirectionsOutput = {
  directions: [],
  summary: {
    potentialDirectionCount: 0,
    bothEvidencePresentCount: 0,
    potentialCustomerContactCount: 0,
  },
};

/**
 * Industry → fixture map, read by MockIvtHubClient.listRadarDirections. The
 * dispatcher resolves an unknown industry to the HCLS fixture so dev-mode
 * previews default to the richest state.
 */
export const listRadarDirectionsFixtures: Record<string, ListRadarDirectionsOutput> = {
  HCLS: RADAR_HCLS,
  FSI: RADAR_FSI,
  GAME: RADAR_GAME,
};

export const listRadarDirectionsMock: ListRadarDirectionsOutput = RADAR_HCLS;

/**
 * GetRadarDirection detail fixtures (DAT-9115, R18). One fixture per state
 * the Figma E modal must survive — read by MockIvtHubClient.send on the
 * command class name. Keyed by `directionId` so the mock can look the
 * detail up from a click on the matching ListRadarDirections card title.
 *
 * `industry` on these details uses `LS_HC` — the real industry_meta code
 * for Life Sciences / Healthcare per Fan's IVTHub commit f9ab4a7 (merged
 * 2026-10-30). The R17 list fixture carries `HCLS` because it predates that
 * commit; the mismatch is intentional and the look-up is on directionId,
 * not industry. New fixtures use `LS_HC`; R17's fixtures stay as-is (they
 * are not this CR's scope) and will be renamed in a follow-on.
 *
 * States covered (brief, 2026-10-30):
 *   mockdirhcls001  — rich/BOTH: many memberOpps + news + orgs + reusable asset
 *   mockdirhcls002  — long quotes in memberNews.quoteSummary (1024-char cap)
 *   mockdirhcls003  — empty coverage: empty reusableAssets + no news (empty)
 *   mockdirfsi001   — PIPELINE_ONLY: no news, no suggestedOrgs
 *   mockdirfsi002   — minimal: every list empty except memberOpps
 *
 * All strings are synthetic — see the file-header invariant. gap_note /
 * hint are not represented because the wire does not carry them in v1
 * (see DECISIONS.md "Open questions").
 */
// Parenthesize the whole concatenation BEFORE repeating. Without the
// outer parens, `.repeat(N)` binds only to the LAST string literal
// (operator precedence: `.` beats `+`), and 6 copies of 100-ish CJK
// code points plus one copy of the other four lines never reaches the
// 1024 cap — the `.slice(0, 1024)` below would be a no-op. 6 copies of
// the full ~230-cp paragraph easily clears 1024 so the clamp exercises
// the boundary.
const LONG_QUOTE = (
  '方案草案由模型生成、医学团队审核,内部试点已覆盖三个治疗领域;' +
  '多家申办方同期启动,表明这不是个别团队的尝试,而是行业级别的转向。' +
  '访谈笔记:(1)历史试验数据 + 入组标准 LLM 草拟 →(2)医学团队回路审核 →' +
  '(3)数字研发中心联调上线。关键痛点:资料来源散落在多个 CRO 的内部系统里,' +
  '合规审批也要走多级流程;预算侧单条管线提到的额度与近 90 天同类诉求一致。'
).repeat(6);

const RADAR_DETAIL_HCLS_001: GetRadarDirectionOutput = {
  direction: {
    directionId: 'mockdirhcls001',
    industry: 'LS_HC',
    topicId: 'mocktopichcls1',
    topicTitle: '创新药研发主题',
    name: 'AI 驱动的临床试验方案设计',
    rationale:
      '近 90 天 5 家不同 Account 独立出现同一诉求:用 LLM 从历史试验数据生成 protocol 草案 + 患者入组标准。' +
      '5 家中 3 家已在 Technical Validation 阶段;新闻侧两条 Google News 命中同一关键词。' +
      '同期外部也有 2 条独立信源指向同一件事 —— 内部预算与外部动向同时印证,这是本节唯一两条证据都硬的方向之一。',
    status: RadarDirectionStatus.CANDIDATE,
    evidenceTier: RadarEvidenceTier.BOTH,
    pipelineEvidence: {
      distinctAccounts: 5,
      oppCount: 7,
      totalArrUsd: 1_340_000,
      technicalValidationAccountCount: 3,
    },
    accountChips: [
      { accountName: 'MOCK Pharma A', totalArrUsd: 420_000 },
      { accountName: 'MOCK Pharma B', totalArrUsd: 360_000 },
      { accountName: 'MOCK Pharma C', totalArrUsd: 280_000 },
      { accountName: 'MOCK Pharma D', totalArrUsd: 180_000 },
      { accountName: 'MOCK Pharma E', totalArrUsd: 100_000 },
    ],
    memberOpps: [
      {
        opptyId: 'MOCK-OPP-001',
        accountName: 'MOCK Pharma A',
        oppName: 'MOCK AZ-临床方案 AI 生成 POC',
        arrUsd: 420_000,
        lastActivityDate: '2026-07-22',
        stage: '3.a Technical Validation',
        cell: 'MOCK LS_HC Cell A',
      },
      {
        opptyId: 'MOCK-OPP-002',
        accountName: 'MOCK Pharma B',
        oppName: 'MOCK Protocol 草案 LLM 评测',
        arrUsd: 360_000,
        lastActivityDate: '2026-07-10',
        stage: '3.a Technical Validation',
        cell: 'MOCK LS_HC Cell A',
      },
      {
        opptyId: 'MOCK-OPP-003',
        accountName: 'MOCK Pharma C',
        oppName: 'MOCK 患者入组标准生成',
        arrUsd: 280_000,
        lastActivityDate: '2026-06-30',
        stage: '3.a Technical Validation',
        cell: 'MOCK LS_HC Cell B',
      },
      {
        opptyId: 'MOCK-OPP-004',
        accountName: 'MOCK Pharma D',
        oppName: 'MOCK 临床运营 LLM 调研',
        arrUsd: 180_000,
        // No last activity on this opp — pins the dash-fallback branch.
        stage: '2.b Discovery',
        cell: 'MOCK LS_HC Cell B',
      },
      {
        opptyId: 'MOCK-OPP-005',
        accountName: 'MOCK Pharma E',
        oppName: 'MOCK 跨国申办方试点',
        // ARR absent — pins the ARR dash-fallback branch too.
        lastActivityDate: '2026-05-20',
        stage: '2.b Discovery',
        // cell null — exercises the cell dash-fallback.
      },
      {
        opptyId: 'MOCK-OPP-006',
        accountName: 'MOCK Pharma A',
        oppName: 'MOCK AZ-临床方案 AI 第二期',
        arrUsd: 150_000,
        lastActivityDate: '2026-07-18',
        stage: '2.b Discovery',
        cell: 'MOCK LS_HC Cell A',
      },
      {
        opptyId: 'MOCK-OPP-007',
        accountName: 'MOCK Pharma B',
        oppName: 'MOCK Protocol LLM 二期扩展',
        arrUsd: 90_000,
        lastActivityDate: '2026-07-05',
        stage: '1.a Prospect',
        cell: 'MOCK LS_HC Cell A',
      },
    ],
    memberNews: [
      {
        outlet: '医药行业媒体(示意)',
        quoteSummary:
          '方案草案由模型生成、医学团队审核,内部试点已覆盖三个治疗领域',
        url: 'https://example.invalid/news/mock-protocol-llm',
        publishedAt: '2026-07-19',
      },
      {
        outlet: 'MOCK Industry Daily',
        quoteSummary: '中国区临床运营数字化年度规划发布,大模型协助试验方案生成成为关键',
        url: 'https://example.invalid/news/mock-china-trial',
        publishedAt: '2026-06-11',
      },
    ],
    suggestedOrgs: [
      {
        name: 'MOCK 某国家级药物临床试验机构联盟',
        note: '非 AWS 客户',
        side: RadarSuggestedOrgSide.NAMED_IN_NEWS,
      },
      {
        name: 'MOCK CRO Alpha',
        note: 'AWS 客户 · 本方向 0 笔',
        side: RadarSuggestedOrgSide.AWS_CUSTOMER_NO_PIPELINE,
        accountId: '001MOCKCROALPHA',
      },
      {
        name: 'MOCK CRO Beta',
        note: 'AWS 客户 · 本方向 0 笔',
        side: RadarSuggestedOrgSide.AWS_CUSTOMER_NO_PIPELINE,
        accountId: '001MOCKCROBETA',
      },
    ],
    // One reusable asset — the inline coverage row should render.
    reusableAssets: [
      {
        assetId: 'mock-asset-protocol-llm',
        industry: 'LS_HC',
        assetName: 'MOCK 临床方案 LLM 资产(可借鉴)',
      },
    ],
    emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    needs: [
      {
        needId: 'MOCK-NEED-A1',
        needText: 'LLM 从历史试验数据生成 protocol 草案 + 患者入组标准',
        quote: 'MOCK: 希望用 LLM 从历史数据反推出标准化 protocol',
        evidence: RadarNeedEvidence.OPP_ACTIVITY,
        evidenceDate: '2026-07-22',
        opptyId: 'MOCK-OPP-001',
      },
    ],
    createdAt: new Date('2026-10-01T00:00:00Z'),
    updatedAt: new Date('2026-10-25T00:00:00Z'),
  },
};

const RADAR_DETAIL_HCLS_002: GetRadarDirectionOutput = {
  direction: {
    directionId: 'mockdirhcls002',
    industry: 'LS_HC',
    topicId: 'mocktopichcls2',
    topicTitle: '器械创新主题',
    name: '手术机器人视觉模型本地部署',
    rationale: '新闻侧 3 条独立报道提及同一需求;管线侧暂无成交。',
    status: RadarDirectionStatus.CANDIDATE,
    evidenceTier: RadarEvidenceTier.NEWS_ONLY,
    pipelineEvidence: {
      distinctAccounts: 0,
      oppCount: 0,
      totalArrUsd: 0,
      technicalValidationAccountCount: 0,
    },
    accountChips: [],
    memberOpps: [],
    memberNews: [
      {
        outlet: 'MOCK 器械行业周刊',
        // Long quote fixture — exercises wrap/scroll on the 摘要引述 column.
        // Clamped at the smithy @length(max: 1024).
        quoteSummary: LONG_QUOTE.slice(0, 1024),
        url: 'https://example.invalid/news/mock-surgical-robot',
        publishedAt: '2026-08-02',
      },
      {
        outlet: 'MOCK 行业分析(手动上传)',
        quoteSummary: '器械注册新规下的本地化部署路径,内部摘要',
        // url intentionally absent — pins the "原文" em-dash branch.
        publishedAt: '2026-07-25',
      },
    ],
    suggestedOrgs: [
      {
        name: 'MOCK 某头部国产器械厂商',
        side: RadarSuggestedOrgSide.NAMED_IN_NEWS,
        // note nullable (string_agg returns NULL across empty mentioned_as).
      },
    ],
    reusableAssets: [],
    emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    needs: [],
    createdAt: new Date('2026-10-01T00:00:00Z'),
    updatedAt: new Date('2026-10-20T00:00:00Z'),
  },
};

const RADAR_DETAIL_HCLS_003: GetRadarDirectionOutput = {
  direction: {
    directionId: 'mockdirhcls003',
    industry: 'LS_HC',
    topicId: 'mocktopicgenhcls',
    topicTitle: '其他 / 待归主题',
    name: '区域医保结算智能稽核',
    rationale: '来自 4 家不同 Account 的近期活动笔记提到同一问题。',
    status: RadarDirectionStatus.CANDIDATE,
    evidenceTier: RadarEvidenceTier.PIPELINE_ONLY,
    pipelineEvidence: {
      distinctAccounts: 4,
      oppCount: 6,
      totalArrUsd: 820_000,
      technicalValidationAccountCount: 1,
    },
    accountChips: [
      { accountName: 'MOCK 省级医保局 A', totalArrUsd: 300_000 },
      { accountName: 'MOCK 省级医保局 B', totalArrUsd: 220_000 },
    ],
    memberOpps: [
      {
        opptyId: 'MOCK-OPP-H3-01',
        accountName: 'MOCK 省级医保局 A',
        oppName: 'MOCK 医保结算智能稽核 POC',
        arrUsd: 300_000,
        lastActivityDate: '2026-07-01',
        stage: '3.a Technical Validation',
        cell: 'MOCK LS_HC Cell C',
      },
      {
        opptyId: 'MOCK-OPP-H3-02',
        accountName: 'MOCK 省级医保局 B',
        oppName: 'MOCK 结算风控联合调研',
        arrUsd: 220_000,
        lastActivityDate: '2026-06-25',
        stage: '2.b Discovery',
        cell: 'MOCK LS_HC Cell C',
      },
    ],
    // No news for this direction (news-side empty-state inside a modal).
    memberNews: [],
    suggestedOrgs: [],
    // No reusable assets — coverage row stays hidden.
    reusableAssets: [],
    emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    needs: [],
    createdAt: new Date('2026-10-01T00:00:00Z'),
    updatedAt: new Date('2026-10-18T00:00:00Z'),
  },
};

const RADAR_DETAIL_FSI_001: GetRadarDirectionOutput = {
  direction: {
    directionId: 'mockdirfsi001',
    industry: 'FSI',
    topicId: 'mocktopicgenfsi',
    topicTitle: '其他 / 待归主题',
    name: '账户欺诈模式识别',
    rationale: '来自 3 家券商的近 90 天活动笔记里同一诉求出现 7 次。',
    status: RadarDirectionStatus.CANDIDATE,
    evidenceTier: RadarEvidenceTier.PIPELINE_ONLY,
    pipelineEvidence: {
      distinctAccounts: 3,
      oppCount: 4,
      totalArrUsd: 560_000,
      technicalValidationAccountCount: 2,
    },
    accountChips: [
      { accountName: 'MOCK 券商 A', totalArrUsd: 240_000 },
      { accountName: 'MOCK 券商 B', totalArrUsd: 200_000 },
      { accountName: 'MOCK 券商 C', totalArrUsd: 120_000 },
    ],
    memberOpps: [
      {
        opptyId: 'MOCK-OPP-F1-01',
        accountName: 'MOCK 券商 A',
        oppName: 'MOCK 欺诈模式 ML POC',
        arrUsd: 240_000,
        lastActivityDate: '2026-07-15',
        stage: '3.a Technical Validation',
        cell: 'MOCK FSI Cell',
      },
    ],
    memberNews: [],
    suggestedOrgs: [],
    reusableAssets: [],
    emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    needs: [],
    createdAt: new Date('2026-10-01T00:00:00Z'),
    updatedAt: new Date('2026-10-20T00:00:00Z'),
  },
};

const RADAR_DETAIL_FSI_002: GetRadarDirectionOutput = {
  direction: {
    directionId: 'mockdirfsi002',
    industry: 'FSI',
    topicId: 'mocktopicgenfsi',
    topicTitle: '其他 / 待归主题',
    name: '财险理赔 GenAI 预审',
    rationale: '来自 2 家保险公司的近期活动笔记里一致的 GenAI 预审诉求。',
    status: RadarDirectionStatus.CANDIDATE,
    evidenceTier: RadarEvidenceTier.PIPELINE_ONLY,
    pipelineEvidence: {
      distinctAccounts: 2,
      oppCount: 3,
      totalArrUsd: 410_000,
      technicalValidationAccountCount: 0,
    },
    accountChips: [{ accountName: 'MOCK 保险 D', totalArrUsd: 260_000 }],
    memberOpps: [
      {
        opptyId: 'MOCK-OPP-F2-01',
        accountName: 'MOCK 保险 D',
        oppName: 'MOCK 理赔预审 LLM 调研',
        arrUsd: 260_000,
        lastActivityDate: '2026-07-05',
        stage: '2.b Discovery',
        cell: 'MOCK FSI Cell',
      },
    ],
    memberNews: [],
    suggestedOrgs: [],
    reusableAssets: [],
    emptyNewsState: RadarEmptyNewsState.HAS_NEWS,
    needs: [],
    createdAt: new Date('2026-10-01T00:00:00Z'),
    updatedAt: new Date('2026-10-20T00:00:00Z'),
  },
};

/**
 * directionId → detail fixture map, read by MockIvtHubClient on
 * GetRadarDirectionCommand. An unknown directionId falls back to
 * `getRadarDirectionMock` (the HCLS rich fixture) so dev-mode previews are
 * never left staring at a null direction.
 */
export const getRadarDirectionFixtures: Record<string, GetRadarDirectionOutput> = {
  mockdirhcls001: RADAR_DETAIL_HCLS_001,
  mockdirhcls002: RADAR_DETAIL_HCLS_002,
  mockdirhcls003: RADAR_DETAIL_HCLS_003,
  mockdirfsi001: RADAR_DETAIL_FSI_001,
  mockdirfsi002: RADAR_DETAIL_FSI_002,
};

export const getRadarDirectionMock: GetRadarDirectionOutput = RADAR_DETAIL_HCLS_001;

/**
 * STO Dashboard flow stubs — zero-pipeline response per view (DAT-9115,
 * R18 scope-adjacent). Without these, `bb server:ivt-mock` crashes the
 * dashboard before the radar section (DashboardPage's content block
 * gates on `stoFlow.data`/`gmFlow.data`), which blocks verifying any
 * part of the dashboard — including this CR's R18 modal — on fixtures.
 * The two shapes are a MINIMAL non-null response: empty row lists,
 * zeroed totals, the real window the hook sent. The dashboard renders
 * its empty-state surfaces (no-permission etc.) when either list is
 * empty, which is fine for mock-mode previews — the radar section
 * does not depend on these fields.
 */
const ZERO_BUCKET = { oppCount: 0, arr: 0 };
const ZERO_TOTAL = {
  metrics: {
    momentum: ZERO_BUCKET,
    base: ZERO_BUCKET,
    open: ZERO_BUCKET,
    launched: ZERO_BUCKET,
    lost: ZERO_BUCKET,
    notFollowedUp: ZERO_BUCKET,
  },
  vitality: { red: 0, amber: 0, green: 0 },
};

export const getAssetPipelineFlowMock: GetAssetPipelineFlowOutput = {
  window: { start: '2026-07-01', end: '2026-09-30' },
  total: ZERO_TOTAL,
  assets: [],
};

export const getIndustryPipelineFlowMock: GetIndustryPipelineFlowOutput = {
  window: { start: '2026-07-01', end: '2026-09-30' },
  total: ZERO_TOTAL,
  industries: [],
};
// ── Radar Admin (R16) ────────────────────────────────────────────────────────
// Seed read-fixtures for the three Admin list ops under /ivt/radar-config.
// MockIvtHubClient copies these into module-scope mutable state on first
// use, so the dev-mode page can round-trip Upsert / SetStatus without the
// backend (see MockIvtHubClient.radarConfigState in mockClient.ts).
//
// Industry-scoped. Only HCLS is seeded with topics + a GENERAL_TOPIC — it
// is the one industry topic-model §6 v1 seeds for real; FSI / GAME get an
// empty-list payload so the Admin page can be exercised against a bare
// industry, i.e. before any topics are configured, which is the state
// AUTO / GAMES / CROSS-ENE will start in when they migrate in R5 / R16.

const MOCK_RADAR_UPDATED_AT = new Date('2026-10-29T04:17:00Z');

const HCLS_TOPIC_1: RadarStandardTopic = {
  topicId: 'MOCK-RADAR-TOPIC-HCLS-01',
  title: '医疗器械',
  searchKeywords: ['Class II', 'FDA 510(k)', 'NMPA'],
  excludeKeywords: ['动物医疗器械'],
  extractionRule: '仅抽取 Class II / Class III 器械相关需求，剔除消费级产品评测。',
  inductionRule: '按器械类型 (影像 / 介入 / 诊断) 做方向归纳，而非按客户细分。',
  displayOrder: 1,
  status: 'ACTIVE',
  createdBy: 'mock-admin',
  createdAt: MOCK_RADAR_UPDATED_AT,
  updatedBy: 'mock-admin',
  updatedAt: MOCK_RADAR_UPDATED_AT,
};

const HCLS_TOPIC_2: RadarStandardTopic = {
  topicId: 'MOCK-RADAR-TOPIC-HCLS-02',
  title: '医疗机构',
  searchKeywords: ['三甲医院', '互联网医院'],
  excludeKeywords: [],
  extractionRule: '抽取涉及医疗机构 IT 建设 / 数据合规 / AI 临床应用的需求。',
  inductionRule: '按机构类型分方向：三甲 / 二甲 / 民营医疗 / 互联网医院。',
  displayOrder: 2,
  status: 'ACTIVE',
  createdBy: 'mock-admin',
  createdAt: MOCK_RADAR_UPDATED_AT,
  updatedBy: 'mock-admin',
  updatedAt: MOCK_RADAR_UPDATED_AT,
};

const HCLS_GENERAL: RadarGeneralTopic = {
  topicId: 'MOCK-RADAR-GENERAL-HCLS',
  title: '全部内容',
  searchKeywords: ['医疗健康', '生物医药', 'HCLS'],
  excludeKeywords: ['二手器械转卖', '虚假医疗广告'],
  extractionRule: '通用抽取：行业 HCLS 范围内的新需求，兜底 Fan 的分类。',
  inductionRule: '方向侧通用归纳：覆盖未命中标准主题的内容。',
  status: 'ACTIVE',
  createdBy: 'mock-admin',
  createdAt: MOCK_RADAR_UPDATED_AT,
  updatedBy: 'mock-admin',
  updatedAt: MOCK_RADAR_UPDATED_AT,
};

const HCLS_NEWS_SOURCES: RadarNewsSource[] = [
  {
    sourceId: 'MOCK-RADAR-NEWS-SRC-HCLS-01',
    title: 'Google News · HCLS 关键词',
    sourceKind: 'GOOGLE_NEWS_KEYWORD',
    sourceLocale: 'zh-CN',
    status: 'ACTIVE',
    createdBy: 'mock-admin',
    createdAt: MOCK_RADAR_UPDATED_AT,
    updatedBy: 'mock-admin',
    updatedAt: MOCK_RADAR_UPDATED_AT,
  },
  {
    sourceId: 'MOCK-RADAR-NEWS-SRC-HCLS-02',
    title: '《健康界》官网',
    sourceKind: 'GOOGLE_NEWS_SITE',
    sourceLocator: 'cn-healthcare.com',
    sourceLocale: 'zh-CN',
    status: 'ACTIVE',
    createdBy: 'mock-admin',
    createdAt: MOCK_RADAR_UPDATED_AT,
    updatedBy: 'mock-admin',
    updatedAt: MOCK_RADAR_UPDATED_AT,
  },
];

/** Industry → initial topics list. HCLS seeded; others empty. */
export const radarConfigTopicsFixtures: Record<string, RadarStandardTopic[]> = {
  HCLS: [HCLS_TOPIC_1, HCLS_TOPIC_2],
  FSI: [],
  GAME: [],
};

/**
 * Industry → initial GENERAL_TOPIC row. `null` for an industry whose
 * GENERAL_TOPIC seed migration has not run yet — the Admin page handles
 * that as "GENERAL has not been seeded, re-fetch after Fan's seed script",
 * i.e. the empty-state ListIndustryGeneralTopic reads.
 */
export const radarConfigGeneralFixtures: Record<string, RadarGeneralTopic | null> = {
  HCLS: HCLS_GENERAL,
  FSI: null,
  GAME: null,
};

/** Industry → initial NEWS_SOURCE list. */
export const radarConfigNewsSourcesFixtures: Record<string, RadarNewsSource[]> = {
  HCLS: HCLS_NEWS_SOURCES,
  FSI: [],
  GAME: [],
};

export const listIndustryTopicsMock: ListIndustryTopicsOutput = {
  topics: radarConfigTopicsFixtures.HCLS,
};
export const getIndustryGeneralTopicMock: GetIndustryGeneralTopicOutput = {
  generalTopic: radarConfigGeneralFixtures.HCLS ?? undefined,
};
export const listIndustryNewsSourcesMock: ListIndustryNewsSourcesOutput = {
  sources: radarConfigNewsSourcesFixtures.HCLS,
};
